// smartcampusdashborad.js - Full code with animated background circles

import { useState, useEffect, useRef } from "react";
import StudentView from "./studentView";
import { LayoutDashboard, Users, GraduationCap, GraduationCap as GraduationCapIcon, CreditCard, Megaphone, CheckSquare, CalendarDays, CalendarDays as CalendarDaysIcon, ClipboardList, ClipboardList as ClipboardListIcon, UserCircle, Menu, X, X as XIcon, ChevronDown, ChevronUp, Coins, Coins as CoinsIcon, Gift, Gift as GiftIcon, LogOut, ArrowLeft, Crown, User, Lock, Calendar, Search, BarChart3, RefreshCw, ArrowRight, Clock, Check, AlertTriangle, FlaskConical, Monitor, Palette, ArrowUp, ArrowDown, EyeOff, Eye, CheckCircle2, HelpCircle, Mail, ThumbsUp, XCircle, Package, FileEdit } from "lucide-react";
import Circular from "./circular";
import SalaryManagement from "./SalaryManagement";
import PrincipalSalaryView from "./PrincipalSalaryView";
import Footer from "./Footer";
import { useSchoolName } from "./schoolNameStore";

// ═══════════════════════════════════════════════════════════════════════════════
//  MODERN ENHANCED SMART CAMPUS DASHBOARD — PRINCIPAL SIDE
//  Features: Glassmorphism, Smooth Animations, Gradient Accents, Micro-interactions
//  All original functionality preserved — Blue theme + Sidebar navigation
//  COLORS: #0D47A1, #2196F3, #90CAF9, #E3F2FD ONLY
// ═══════════════════════════════════════════════════════════════════════════════

// ─── GLOBAL STYLES (injected via useEffect) ───────────────────────────────────

const injectStyles = () => {
  if (document.getElementById('campus-dashboard-styles')) return;
  const style = document.createElement('style');
  style.id = 'campus-dashboard-styles';
  style.textContent = `
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeInScale {
      from { opacity: 0; transform: scale(0.95); }
      to { opacity: 1; transform: scale(1); }
    }
    @keyframes slideInRight {
      from { opacity: 0; transform: translateX(30px); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-6px); }
    }
    @keyframes shimmer {
      0% { background-position: -1000px 0; }
      100% { background-position: 1000px 0; }
    }
    @keyframes gradient-shift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    @keyframes toast-in {
      from { opacity: 0; transform: translateX(100%); }
      to { opacity: 1; transform: translateX(0); }
    }
    @keyframes progress-fill {
      from { width: 0%; }
    }
    @keyframes count-up {
      from { opacity: 0; transform: translateY(10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes circle-progress {
      from { stroke-dashoffset: 314; }
      to { stroke-dashoffset: var(--target); }
    }
    @keyframes pie-rotate {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes rotateCircle {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes pulseCircle {
      0%, 100% { transform: scale(1); opacity: 0.8; }
      50% { transform: scale(1.05); opacity: 1; }
    }
    @keyframes borderPulse {
      0%, 100% { border-color: rgba(13, 71, 161, 0.3); }
      50% { border-color: rgba(33, 150, 243, 0.8); }
    }
    @keyframes floatParticle {
      0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.3; }
      25% { transform: translateY(-10px) translateX(5px); opacity: 0.6; }
      50% { transform: translateY(-20px) translateX(-5px); opacity: 0.8; }
      75% { transform: translateY(-10px) translateX(10px); opacity: 0.6; }
    }
    @keyframes shineSweep {
      0% { transform: translateX(-150%) skewX(-15deg); }
      100% { transform: translateX(250%) skewX(-15deg); }
    }
    @keyframes meshDrift {
      0%, 100% { background-position: 0% 0%, 100% 100%, 50% 50%; }
      50% { background-position: 15% 10%, 85% 90%, 55% 45%; }
    }
    @keyframes glowPulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(33,150,243,0.0); }
      50% { box-shadow: 0 0 30px 4px rgba(33,150,243,0.15); }
    }
    @keyframes underlineGrow {
      from { transform: scaleX(0); }
      to { transform: scaleX(1); }
    }
    @keyframes blobFloatA {
      0%, 100% { transform: translate(0px, 0px) scale(1); }
      50% { transform: translate(40px, 30px) scale(1.08); }
    }
    @keyframes blobFloatB {
      0%, 100% { transform: translate(0px, 0px) scale(1); }
      50% { transform: translate(-35px, -25px) scale(1.05); }
    }
    @keyframes blobFloatC {
      0%, 100% { transform: translate(0px, 0px) scale(1); }
      50% { transform: translate(25px, -35px) scale(1.1); }
    }
    @keyframes gridDrift {
      0% { background-position: 0px 0px; }
      100% { background-position: 40px 40px; }
    }
    .animate-blob-a { animation: blobFloatA 14s ease-in-out infinite; }
    .animate-blob-b { animation: blobFloatB 18s ease-in-out infinite; }
    .animate-blob-c { animation: blobFloatC 16s ease-in-out infinite; }
    .animate-grid-drift { animation: gridDrift 30s linear infinite; }
    .glass-panel {
      background: rgba(255, 255, 255, 0.75);
      backdrop-filter: blur(16px);
      -webkit-backdrop-filter: blur(16px);
      border: 1px solid rgba(255, 255, 255, 0.5);
    }
    .glass-dark {
      background: rgba(15, 23, 42, 0.85);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
    .card-hover {
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .card-hover:hover {
      transform: translateY(-4px);
      box-shadow: 0 20px 40px -12px rgba(13, 71, 161, 0.18);
    }
    .animate-fade-in-up {
      animation: fadeInUp 0.5s ease-out forwards;
    }
    .animate-fade-in-scale {
      animation: fadeInScale 0.4s ease-out forwards;
    }
    .animate-slide-in-right {
      animation: slideInRight 0.4s ease-out forwards;
    }
    .animate-float {
      animation: float 3s ease-in-out infinite;
    }
    .animate-toast-in {
      animation: toast-in 0.3s ease-out forwards;
    }
    .animate-gradient {
      background-size: 200% 200%;
      animation: gradient-shift 8s ease infinite;
    }
    .animate-rotate-circle {
      animation: rotateCircle 20s linear infinite;
    }
    .animate-pulse-circle {
      animation: pulseCircle 3s ease-in-out infinite;
    }
    .animate-border-pulse {
      animation: borderPulse 2s ease-in-out infinite;
    }
    .animate-float-particle {
      animation: floatParticle 6s ease-in-out infinite;
    }
    .stagger-1 { animation-delay: 0.05s; }
    .stagger-2 { animation-delay: 0.1s; }
    .stagger-3 { animation-delay: 0.15s; }
    .stagger-4 { animation-delay: 0.2s; }
    .stagger-5 { animation-delay: 0.25s; }
    .stagger-6 { animation-delay: 0.3s; }
    .timetable-viewport { max-width: 380px; overflow-x: auto; -webkit-overflow-scrolling: touch; }
    @media (min-width: 768px) { .timetable-viewport { max-width: none; overflow-x: auto; } }
    .sidebar-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
    .sidebar-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 999px; }

    /* ── Signature: mesh-gradient app backdrop (blue-only) ───────────────── */
    .campus-mesh-bg {
      background-image:
        radial-gradient(circle at 0% 0%, rgba(33,150,243,0.10), transparent 45%),
        radial-gradient(circle at 100% 100%, rgba(13,71,161,0.08), transparent 45%),
        radial-gradient(circle at 50% 50%, rgba(144,202,249,0.10), transparent 55%);
      background-size: 200% 200%, 200% 200%, 160% 160%;
      animation: meshDrift 22s ease-in-out infinite;
    }

    /* ── Signature: glass shine sweep on hover ────────────────────────────── */
    .shine-hover { position: relative; overflow: hidden; }
    .shine-hover::after {
      content: "";
      position: absolute;
      top: 0; left: 0;
      width: 40%; height: 100%;
      background: linear-gradient(115deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%);
      transform: translateX(-150%) skewX(-15deg);
      pointer-events: none;
    }
    .shine-hover:hover::after {
      animation: shineSweep 0.9s ease;
    }

    .glow-hover:hover { animation: glowPulse 1.6s ease-in-out infinite; }

    .grow-underline {
      position: relative;
    }
    .grow-underline::after {
      content: "";
      position: absolute;
      left: 0; right: 0; bottom: -4px;
      height: 2px;
      background: linear-gradient(90deg, #2196F3, #90CAF9);
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 0.35s ease;
    }
    .grow-underline:hover::after { transform: scaleX(1); }

    .glass-panel-deep {
      background: rgba(255, 255, 255, 0.65);
      backdrop-filter: blur(20px) saturate(140%);
      -webkit-backdrop-filter: blur(20px) saturate(140%);
      border: 1px solid rgba(144, 202, 249, 0.35);
      box-shadow: 0 8px 32px -8px rgba(13,71,161,0.12), inset 0 1px 0 rgba(255,255,255,0.6);
    }

    .stat-card-noise {
      background-image: radial-gradient(rgba(255,255,255,0.14) 1px, transparent 1px);
      background-size: 14px 14px;
    }

    /* ── Mobile refinements ───────────────────────────────────────────────── */
    @media (max-width: 640px) {
      .glass-panel, .glass-panel-deep { border-radius: 1rem; }
      table { font-size: 12px; }
      .mobile-tap-target { min-height: 44px; }
    }
    @media (prefers-reduced-motion: reduce) {
      * { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
    }

    .pie-container {
      position: relative;
      width: 200px;
      height: 200px;
    }
    .pie-slice {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      clip-path: polygon(50% 50%, 0 0, 100% 0);
      transition: transform 0.3s ease;
    }
    .pie-slice:hover {
      transform: scale(1.05);
      filter: brightness(1.1);
    }
    .pie-label {
      position: absolute;
      font-size: 10px;
      font-weight: 700;
      color: #0f172a;
      text-shadow: 0 1px 2px rgba(255,255,255,0.8);
      pointer-events: none;
    }
  `;
  document.head.appendChild(style);
};

// ─── ANIMATED BACKGROUND CIRCLES ──────────────────────────────────────────────

function AnimatedBackgroundCircles() {
  return <div className="fixed inset-0 pointer-events-none overflow-hidden z-0 bg-[#F4F8FF]"></div>;
}

// ─── DATA ──────────────────────────────────────────────────────────────────────

const allClasses = ["UKG", "LKG", "Class 1", "Class 2", "Class 3", "Class 4", "Class 5", "Class 6", "Class 7", "Class 8", "Class 9", "Class 10", "Class 11", "Class 12"];

// Class order used by the "Enrollment by Fees" sidebar — LKG through Class 12.
const feesClassOrder = ["LKG", "UKG", "Class 1", "Class 2", "Class 3", "Class 4", "Class 5", "Class 6", "Class 7", "Class 8", "Class 9", "Class 10", "Class 11", "Class 12"];
const CLASS_COLORS = [{
  bg: "bg-blue-50",
  text: "text-blue-600",
  border: "border-blue-200",
  bar: "#2196F3",
  gradient: "from-blue-700 to-blue-800",
  light: "#E3F2FD"
}, {
  bg: "bg-blue-50",
  text: "text-blue-600",
  border: "border-blue-200",
  bar: "#2196F3",
  gradient: "from-blue-700 to-blue-800",
  light: "#E3F2FD"
}, {
  bg: "bg-blue-50",
  text: "text-blue-600",
  border: "border-blue-200",
  bar: "#2196F3",
  gradient: "from-blue-700 to-blue-800",
  light: "#E3F2FD"
}, {
  bg: "bg-blue-50",
  text: "text-blue-600",
  border: "border-blue-200",
  bar: "#2196F3",
  gradient: "from-blue-700 to-blue-800",
  light: "#E3F2FD"
}, {
  bg: "bg-blue-50",
  text: "text-blue-600",
  border: "border-blue-200",
  bar: "#2196F3",
  gradient: "from-blue-700 to-blue-800",
  light: "#E3F2FD"
}, {
  bg: "bg-blue-50",
  text: "text-blue-600",
  border: "border-blue-200",
  bar: "#2196F3",
  gradient: "from-blue-700 to-blue-800",
  light: "#E3F2FD"
}, {
  bg: "bg-blue-50",
  text: "text-blue-600",
  border: "border-blue-200",
  bar: "#2196F3",
  gradient: "from-blue-700 to-blue-800",
  light: "#E3F2FD"
}, {
  bg: "bg-blue-50",
  text: "text-blue-600",
  border: "border-blue-200",
  bar: "#2196F3",
  gradient: "from-blue-700 to-blue-800",
  light: "#E3F2FD"
}, {
  bg: "bg-blue-50",
  text: "text-blue-600",
  border: "border-blue-200",
  bar: "#2196F3",
  gradient: "from-blue-700 to-blue-800",
  light: "#E3F2FD"
}, {
  bg: "bg-blue-50",
  text: "text-blue-600",
  border: "border-blue-200",
  bar: "#2196F3",
  gradient: "from-blue-700 to-blue-800",
  light: "#E3F2FD"
}, {
  bg: "bg-blue-50",
  text: "text-blue-600",
  border: "border-blue-200",
  bar: "#2196F3",
  gradient: "from-blue-700 to-blue-800",
  light: "#E3F2FD"
}, {
  bg: "bg-blue-50",
  text: "text-blue-600",
  border: "border-blue-200",
  bar: "#2196F3",
  gradient: "from-blue-700 to-blue-800",
  light: "#E3F2FD"
}, {
  bg: "bg-blue-50",
  text: "text-blue-600",
  border: "border-blue-200",
  bar: "#2196F3",
  gradient: "from-blue-700 to-blue-800",
  light: "#E3F2FD"
}, {
  bg: "bg-blue-50",
  text: "text-blue-600",
  border: "border-blue-200",
  bar: "#2196F3",
  gradient: "from-blue-700 to-blue-800",
  light: "#E3F2FD"
}];
const AV_COLORS = [{
  bg: "bg-gradient-to-br from-blue-600 to-blue-700",
  text: "text-white"
}, {
  bg: "bg-gradient-to-br from-blue-600 to-blue-700",
  text: "text-white"
}, {
  bg: "bg-gradient-to-br from-blue-600 to-blue-700",
  text: "text-white"
}, {
  bg: "bg-gradient-to-br from-blue-600 to-blue-700",
  text: "text-white"
}, {
  bg: "bg-gradient-to-br from-blue-600 to-blue-700",
  text: "text-white"
}, {
  bg: "bg-gradient-to-br from-blue-600 to-blue-700",
  text: "text-white"
}];
const ADMISSION_STATUSES = ["joined", "pending", "approval", "inprogress"];
const PARENT_NAMES = ["Suresh", "Rajan", "Muthu", "Selvam", "Kumar", "Mohan", "Pandi", "Thilak", "Ramesh", "Vijay"];
const studentCache = {};
function getStudents(cls) {
  return studentCache[cls] || [];
}
function getSectionsForClass(cls) {
  const students = getStudents(cls);
  const secs = new Set(['A', 'B', 'C']);
  if (students && students.length > 0) {
    students.forEach(s => {
      if (s.section) secs.add(s.section.toUpperCase());
    });
  }
  return [...secs].sort();
}
const SENIOR_GROUPS = [{
  label: "Bio Maths",
  dept: "Bio Maths",
  subjects: ["Physics", "Chemistry", "Biology", "Mathematics"],
  icon: <FlaskConical className="inline w-[1em] h-[1em] align-[-0.125em]" />,
  bg: "bg-blue-50",
  text: "text-blue-700",
  border: "border-blue-200",
  sectionKey: "A",
  gradient: "from-blue-600 to-blue-700"
}, {
  label: "Computer Science",
  dept: "Computer Science",
  subjects: ["Physics", "Chemistry", "CS & IT", "Mathematics"],
  icon: <Monitor className="inline w-[1em] h-[1em] align-[-0.125em]" />,
  bg: "bg-blue-50",
  text: "text-blue-700",
  border: "border-blue-200",
  sectionKey: "A",
  gradient: "from-blue-600 to-blue-700"
}, {
  label: "Commerce",
  dept: "Commerce",
  subjects: ["Accountancy", "Business Maths", "Commerce", "Economics"],
  icon: <BarChart3 className="inline w-[1em] h-[1em] align-[-0.125em]" />,
  bg: "bg-blue-50",
  text: "text-blue-700",
  border: "border-blue-200",
  sectionKey: "B",
  gradient: "from-blue-600 to-blue-700"
}, {
  label: "Art & Science",
  dept: "Art & Science",
  subjects: ["History", "Geography", "Civics", "Economics"],
  icon: <Palette className="inline w-[1em] h-[1em] align-[-0.125em]" />,
  bg: "bg-blue-50",
  text: "text-blue-700",
  border: "border-blue-200",
  sectionKey: "C",
  gradient: "from-blue-600 to-blue-700"
}];
const HS_SECTIONS = ["A", "B"];

// Department → color theme, used only on the Staff Management page (subject
// group tiles + individual staff avatar cards). Kept separate from
// SENIOR_GROUPS' own bg/text/border so Admissions/Students/Attendance/
// Timetable — which also read SENIOR_GROUPS — are left untouched.
function getStaffDeptTheme(dept) {
  const d = (dept || "").trim().toLowerCase();
  if (d === "bio maths") return {
    bg: "#16a34a",
    border: "#15803d",
    text: "#ffffff",
    subtext: "rgba(255,255,255,0.8)",
    chipText: "#15803d"
  };
  if (d === "computer science") return {
    bg: "#2563eb",
    border: "#1d4ed8",
    text: "#ffffff",
    subtext: "rgba(255,255,255,0.8)",
    chipText: "#1d4ed8"
  };
  if (d === "commerce") return {
    bg: "#9333ea",
    border: "#7e22ce",
    text: "#ffffff",
    subtext: "rgba(255,255,255,0.8)",
    chipText: "#7e22ce"
  };
  if (d === "art & science") return {
    bg: "#dc2626",
    border: "#b91c1c",
    text: "#ffffff",
    subtext: "rgba(255,255,255,0.8)",
    chipText: "#b91c1c"
  };
  // Everyone else — UKG to Class 10 teaching staff, Non-Teaching, Admin
  return {
    bg: "#db2777",
    border: "#be185d",
    text: "#ffffff",
    subtext: "rgba(255,255,255,0.8)",
    chipText: "#be185d"
  };
}
let admissionsLoaded = false;
function populateStudentCacheFromAdmissions(admissions) {
  Object.keys(studentCache).forEach(k => delete studentCache[k]);
  const counters = {};
  admissions.forEach(adm => {
    const cls = normalizeApplyingClass(adm.applyingClass || adm.class || adm.applyingClass);
    let section = (adm.section || '').toString().trim().toUpperCase() || 'A';
    let group = (adm.group || '').toString().trim();
    if (cls === 'UKG' || cls === 'LKG') {
      group = '';
    } else if (cls === 'Class 11' || cls === 'Class 12') {
      const knownGroups = SENIOR_GROUPS.map(g => g.label.toLowerCase());
      const gLower = group.toLowerCase();
      if (!group || !knownGroups.includes(gLower)) {
        if (adm.group && typeof adm.group === 'string') {
          const found = SENIOR_GROUPS.find(g => g.label.toLowerCase() === adm.group.toString().trim().toLowerCase());
          group = found ? found.label : SENIOR_GROUPS[0].label;
        } else {
          group = SENIOR_GROUPS[0].label;
        }
      } else {
        group = SENIOR_GROUPS.find(g => g.label.toLowerCase() === gLower).label;
      }
    } else {
      group = '';
    }
    const key = `${cls}::${section}`;
    counters[key] = (counters[key] || 0) + 1;
    const roll = String(counters[key]).padStart(3, '0');
    const name = adm.studentName || `${cls} Student ${counters[key]}`;
    const feeTotal = typeof adm.totalFee === 'number' && adm.totalFee > 0 ? adm.totalFee : 12000;
    const feePaid = adm.paid || 0;
    const feeStatus = feePaid === 0 ? 'pending' : feePaid < feeTotal ? 'partial' : 'paid';
    const stu = {
      _id: adm._id,
      roll,
      name,
      section,
      cls,
      group: group || '',
      attendance: adm.attendance || '—',
      present: adm.present === undefined ? false : adm.present,
      feeTotal,
      feePaid,
      feeStatus,
      avBg: AV_COLORS[counters[key] % AV_COLORS.length].bg,
      avColor: AV_COLORS[counters[key] % AV_COLORS.length].text,
      admissionStatus: (adm.status || 'Enquiry').toString().toLowerCase(),
      parent: adm.fatherName || adm.motherName || '',
      date: adm.enquiryDate || adm.createdAt || '',
      raw: adm
    };
    if (!studentCache[cls]) studentCache[cls] = [];
    studentCache[cls].push(stu);
  });
}

// ─── UTILITY COMPONENTS ───────────────────────────────────────────────────────

function AnimatedCounter({
  value,
  duration = 1000,
  prefix = "",
  suffix = ""
}) {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const hasAnimated = useRef(false);
  useEffect(() => {
    if (hasAnimated.current) {
      setCount(value);
      return;
    }
    const startTime = Date.now();
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * value));
      if (progress < 1) {
        countRef.current = requestAnimationFrame(animate);
      } else {
        setCount(value);
        hasAnimated.current = true;
      }
    };
    countRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(countRef.current);
  }, [value, duration]);
  return <span>{prefix}{count.toLocaleString()}{suffix}</span>;
}
function GlassCard({
  children,
  className = "",
  hover = true
}) {
  return <div className={`glass-panel-deep rounded-2xl shadow-lg shadow-blue-900/5 ${hover ? 'card-hover' : ''} ${className}`}>
      {children}
    </div>;
}
function GradientBadge({
  text,
  gradient = "from-blue-600 to-blue-700",
  className = ""
}) {
  return <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r ${gradient} shadow-md ${className}`}>
      {text}
    </span>;
}

// ─── LIVE CLOCK ────────────────────────────────────────────────────────────────

function LiveClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const hh = String(time.getHours()).padStart(2, "0");
  const mm = String(time.getMinutes()).padStart(2, "0");
  const ss = String(time.getSeconds()).padStart(2, "0");
  const dayName = time.toLocaleDateString("en-IN", {
    weekday: "short"
  });
  const dateStr = time.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
  return <div className="flex items-center gap-2 xl:gap-3 bg-gradient-to-r from-blue-900 to-blue-900 border border-blue-800/40 rounded-2xl px-3 py-2 xl:px-5 xl:py-3 shadow-xl shadow-blue-900/20">
      <div className="relative">
        <div className="w-2.5 h-2.5 xl:w-3 xl:h-3 rounded-full bg-blue-400 animate-pulse"></div>
        <div className="absolute inset-0 w-2.5 h-2.5 xl:w-3 xl:h-3 rounded-full bg-blue-400 animate-ping opacity-30"></div>
      </div>
      <div className="text-right">
        <div className="font-mono font-bold text-white text-sm xl:text-base tracking-widest leading-none tabular-nums">
          {hh}:{mm}:{ss}
        </div>
        <div className="text-[9px] xl:text-[11px] text-blue-300 mt-0.5 xl:mt-1 font-medium tracking-wide">{dayName}, {dateStr}</div>
      </div>
    </div>;
}

// ─── SHARED COMPONENTS ─────────────────────────────────────────────────────────

function AdmStatusBadge({
  status
}) {
  const map = {
    joined: "bg-blue-50 text-blue-700 border-blue-200",
    pending: "bg-blue-50 text-blue-700 border-blue-200",
    approval: "bg-blue-50 text-blue-700 border-blue-200",
    inprogress: "bg-blue-50 text-blue-700 border-blue-200",
    enquiry: "bg-blue-50 text-blue-600 border-blue-200"
  };
  const labels = {
    joined: "Joined",
    pending: "Pending",
    approval: "Approval",
    inprogress: "In Progress",
    enquiry: "Enquiry"
  };
  return <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider border shadow-sm ${map[status] || "bg-gray-50 text-gray-500 border-gray-200"}`}>
      {labels[status] || status}
    </span>;
}
function StatusBadge({
  status
}) {
  const map = {
    confirmed: "bg-blue-50 text-blue-700 border-blue-200",
    pending: "bg-blue-50 text-blue-700 border-blue-200",
    review: "bg-blue-50 text-blue-700 border-blue-200",
    rejected: "bg-red-50 text-red-700 border-red-200",
    paid: "bg-blue-50 text-blue-700 border-blue-200",
    partial: "bg-blue-50 text-blue-700 border-blue-200",
    sent: "bg-blue-50 text-blue-700 border-blue-200",
    draft: "bg-blue-50 text-blue-700 border-blue-200"
  };
  return <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider border shadow-sm ${map[status] || "bg-gray-50 text-gray-500 border-gray-200"}`}>
      {status}
    </span>;
}
function BackBtn({
  onClick,
  label = "Back"
}) {
  return <button onClick={onClick} className="group inline-flex items-center gap-3 text-lg font-extrabold text-slate-800 mb-5 transition-all duration-200 px-4 py-3 rounded-xl hover:bg-blue-50 mobile-tap-target">
      <span className="text-2xl leading-none transform group-hover:-translate-x-1 transition-transform duration-200"><ArrowLeft className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
      <span className="text-lg font-extrabold">{label}</span>
    </button>;
}
function StatCard({
  icon,
  value,
  label,
  trend,
  trendUp,
  gradient
}) {
  return <div className={`rounded-2xl p-5 sm:p-6 relative overflow-hidden text-white shadow-xl ${gradient} card-hover shine-hover stat-card-noise`}>
      <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16 blur-2xl"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full translate-y-12 -translate-x-12 blur-xl"></div>
      <div className="relative z-10">
        <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-xl mb-4 shadow-inner">
          {icon}
        </div>
        <div className="text-2xl sm:text-3xl font-bold leading-none tracking-tight">{value}</div>
        <div className="text-[11px] text-white/80 mt-2 uppercase tracking-wider font-semibold">{label}</div>
        <div className={`absolute top-4 right-4 text-[11px] font-bold px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm border border-white/10 ${trendUp ? "text-blue-200" : "text-red-200"}`}>
          {trendUp ? <ArrowUp className="inline w-[1em] h-[1em] align-[-0.125em]" /> : <ArrowDown className="inline w-[1em] h-[1em] align-[-0.125em]" />} {trend}
        </div>
      </div>
    </div>;
}

// ─── CIRCULAR PROGRESS CHART ─────────────────────────────────────────────────

function CircularProgressChart({
  data
}) {
  const size = 120;
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;
  const colors = [{
    stroke: "#2196F3",
    bg: "#E3F2FD",
    label: "Class 10"
  }, {
    stroke: "#0D47A1",
    bg: "#E3F2FD",
    label: "Class 11"
  }, {
    stroke: "#2196F3",
    bg: "#E3F2FD",
    label: "Class 12"
  }];
  const total = data.reduce((sum, d) => sum + d.count, 0) || 1;
  return <div className="flex flex-wrap items-center justify-center gap-6 md:gap-8">
      {data.map((item, index) => {
      const percentage = total > 0 ? Math.round(item.count / total * 100) : 0;
      const offset = circumference - percentage / 100 * circumference;
      const color = colors[index % colors.length];
      return <div key={index} className="flex flex-col items-center">
            <div className="relative" style={{
          width: size,
          height: size
        }}>
              <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle cx={center} cy={center} r={radius} fill="none" stroke={color.bg} strokeWidth={strokeWidth} />
                <circle cx={center} cy={center} r={radius} fill="none" stroke={color.stroke} strokeWidth={strokeWidth} strokeLinecap="round" style={{
              strokeDasharray: circumference,
              strokeDashoffset: offset,
              transition: "stroke-dashoffset 1.5s ease-in-out"
            }} />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-xl font-extrabold text-slate-800">
                  <AnimatedCounter value={percentage} suffix="%" />
                </span>
                <span className="text-[10px] font-medium text-slate-500 mt-0.5">
                  {item.count} students
                </span>
              </div>
            </div>
            <span className="text-xs font-bold text-slate-600 mt-2">{color.label}</span>
          </div>;
    })}
    </div>;
}

// ─── PIE CHART FOR MARKS DISTRIBUTION ────────────────────────────────────────

function MarksPieChart({
  data
}) {
  const colors = [{
    bg: "#0D47A1",
    label: "A+ (90-100%)",
    color: "#0D47A1"
  }, {
    bg: "#2196F3",
    label: "A (80-89%)",
    color: "#0D47A1"
  }, {
    bg: "#90CAF9",
    label: "B (70-79%)",
    color: "#0D47A1"
  }, {
    bg: "#0D47A1",
    label: "C (60-69%)",
    color: "#0D47A1"
  }, {
    bg: "#2196F3",
    label: "D (Below 60%)",
    color: "#0D47A1"
  }];
  const total = data.reduce((sum, d) => sum + d.count, 0) || 1;
  const radius = 80;
  const centerX = 100;
  const centerY = 100;
  let startAngle = -90;
  return <div className="flex flex-col md:flex-row items-center gap-8">
      <div className="relative" style={{
      width: 200,
      height: 200
    }}>
        <svg width={200} height={200} viewBox="0 0 200 200">
          {data.map((item, index) => {
          const percentage = item.count / total * 100;
          const angle = percentage / 100 * 360;
          const endAngle = startAngle + angle;
          const startRad = startAngle * Math.PI / 180;
          const endRad = endAngle * Math.PI / 180;
          const x1 = centerX + radius * Math.cos(startRad);
          const y1 = centerY + radius * Math.sin(startRad);
          const x2 = centerX + radius * Math.cos(endRad);
          const y2 = centerY + radius * Math.sin(endRad);
          const largeArcFlag = angle > 180 ? 1 : 0;
          const pathData = [`M ${centerX} ${centerY}`, `L ${x1} ${y1}`, `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`, `Z`].join(' ');
          const color = colors[index % colors.length];
          const result = {
            path: pathData,
            color: color.bg,
            label: color.label,
            colorText: color.color,
            percentage: Math.round(percentage),
            count: item.count,
            name: item.grade
          };
          startAngle = endAngle;
          return <g key={index} className="pie-slice-group">
                <path d={result.path} fill={result.color} stroke="#fff" strokeWidth="2" style={{
              transition: 'transform 0.3s ease',
              cursor: 'pointer'
            }} onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'} onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'} />
                {percentage > 10 && <text x={centerX + radius * 0.6 * Math.cos((startRad + endRad) / 2)} y={centerY + radius * 0.6 * Math.sin((startRad + endRad) / 2)} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="12" fontWeight="800" style={{
              textShadow: '0 1px 3px rgba(0,0,0,0.3)',
              pointerEvents: 'none'
            }}>
                    {result.percentage}%
                  </text>}
              </g>;
        })}
        </svg>
      </div>

      <div className="flex flex-col gap-2">
        {data.map((item, index) => {
        const percentage = total > 0 ? Math.round(item.count / total * 100) : 0;
        const color = colors[index % colors.length];
        return <div key={index} className="flex items-center gap-3 text-sm">
              <div className="w-4 h-4 rounded-full flex-shrink-0" style={{
            backgroundColor: color.bg
          }}></div>
              <span className="font-medium text-slate-700">{item.grade}</span>
              <span className="text-slate-500 text-xs">({item.count} students)</span>
              <span className="text-xs font-bold text-slate-600">{percentage}%</span>
            </div>;
      })}
      </div>
    </div>;
}

// ─── FEE TERM PIE CHART (Paid vs Pending, for the Enrollment-by-Fees drilldown) ─

function FeeTermPieChart({
  due,
  paid,
  pending
}) {
  const total = paid + pending || 1;
  const slices = [{
    label: "Paid",
    count: paid,
    color: "#0D47A1"
  }, {
    label: "Pending",
    count: pending,
    color: "#90CAF9"
  }];
  const radius = 80;
  const centerX = 100;
  const centerY = 100;
  let startAngle = -90;
  const paths = slices.map(item => {
    const percentage = item.count / total * 100;
    const angle = percentage / 100 * 360;
    const endAngle = startAngle + angle;
    const startRad = startAngle * Math.PI / 180;
    const endRad = endAngle * Math.PI / 180;
    const midRad = (startAngle + endAngle) / 2 * Math.PI / 180;
    const x1 = centerX + radius * Math.cos(startRad);
    const y1 = centerY + radius * Math.sin(startRad);
    const x2 = centerX + radius * Math.cos(endRad);
    const y2 = centerY + radius * Math.sin(endRad);
    const largeArcFlag = angle > 180 ? 1 : 0;
    const pathData = [`M ${centerX} ${centerY}`, `L ${x1} ${y1}`, `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`, "Z"].join(" ");
    const labelX = centerX + radius * 0.6 * Math.cos(midRad);
    const labelY = centerY + radius * 0.6 * Math.sin(midRad);
    startAngle = endAngle;
    return {
      ...item,
      pathData,
      percentage: Math.round(percentage),
      labelX,
      labelY
    };
  });
  return <div className="flex flex-col md:flex-row items-center gap-8">
      <div className="relative" style={{
      width: 200,
      height: 200
    }}>
        <svg width={200} height={200} viewBox="0 0 200 200">
          {paths.map((p, i) => <g key={i}>
              <path d={p.pathData} fill={p.color} stroke="#fff" strokeWidth="2" style={{
            transition: "transform 0.3s ease",
            cursor: "pointer"
          }} onMouseEnter={e => e.currentTarget.style.transform = "scale(1.05)"} onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"} />
              {p.percentage > 6 && <text x={p.labelX} y={p.labelY} textAnchor="middle" dominantBaseline="middle" fill="#fff" fontSize="13" fontWeight="800" style={{
            textShadow: "0 1px 3px rgba(0,0,0,0.3)",
            pointerEvents: "none"
          }}>
                  {p.percentage}%
                </text>}
            </g>)}
        </svg>
      </div>

      <div className="flex flex-col gap-3">
        {paths.map((p, i) => <div key={i} className="flex items-center gap-3 text-sm">
            <div className="w-4 h-4 rounded-full flex-shrink-0" style={{
          backgroundColor: p.color
        }}></div>
            <span className="font-bold text-slate-700">{p.label}</span>
            <span className="text-slate-500 text-xs">₹{p.count.toLocaleString()}</span>
            <span className="text-xs font-bold text-slate-600">{p.percentage}%</span>
          </div>)}
        <div className="mt-2 pt-3 border-t border-slate-200 text-xs text-slate-500 font-semibold">
          Total Due: ₹{due.toLocaleString()}
        </div>
      </div>
    </div>;
}

// ─── FEES PIE CHART (Paid vs Pending, per class + term) ─────────────────────

function FeesPieChart({
  paid = 0,
  pending = 0,
  percent = 0
}) {
  const total = paid + pending;
  const hasData = total > 0;
  const radius = 70,
    centerX = 90,
    centerY = 90;
  const paidAngle = hasData ? paid / total * 360 : 0;
  const startRad = -90 * Math.PI / 180;
  const endRad = (-90 + paidAngle) * Math.PI / 180;
  const x1 = centerX + radius * Math.cos(startRad);
  const y1 = centerY + radius * Math.sin(startRad);
  const x2 = centerX + radius * Math.cos(endRad);
  const y2 = centerY + radius * Math.sin(endRad);
  const largeArc = paidAngle > 180 ? 1 : 0;
  const paidPath = hasData && paid > 0 ? `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} Z` : "";
  return <div className="flex flex-col sm:flex-row items-center gap-8">
      <div className="relative" style={{
      width: 180,
      height: 180
    }}>
        <svg width={180} height={180} viewBox="0 0 180 180">
          <circle cx={centerX} cy={centerY} r={radius} fill={hasData ? "#90CAF9" : "#E3F2FD"} />
          {paidPath && <path d={paidPath} fill="#0D47A1" stroke="#fff" strokeWidth="2" />}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-extrabold text-slate-800">{hasData ? `${percent}%` : "—"}</span>
          <span className="text-[10px] font-medium text-slate-500 mt-0.5">{hasData ? "Collected" : "No fee data"}</span>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2 text-sm">
          <span className="w-3.5 h-3.5 rounded-full shrink-0" style={{
          background: "#0D47A1"
        }}></span>
          <span className="font-semibold text-slate-700">Paid</span>
          <span className="text-slate-400 text-xs">₹{paid.toLocaleString()}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="w-3.5 h-3.5 rounded-full shrink-0" style={{
          background: "#90CAF9"
        }}></span>
          <span className="font-semibold text-slate-700">Pending</span>
          <span className="text-slate-400 text-xs">₹{pending.toLocaleString()}</span>
        </div>
      </div>
    </div>;
}

// ─── PRINCIPAL DETAILS MODAL ──────────────────────────────────────────────────

function PrincipalDetailsModal({
  onClose
}) {
  const [hmData, setHmData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPw, setShowPw] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("https://schoolcrm-fzcv.onrender.com/api/headmistress");
        if (r.ok) {
          const result = await r.json();
          setHmData(result.data || null);
        }
      } catch {} finally {
        setLoading(false);
      }
    })();
  }, []);
  const nameParts = (hmData?.name || "").trim().split(" ").filter(Boolean);
  const initials = nameParts.length >= 2 ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase() : (hmData?.name || "HM").slice(0, 2).toUpperCase();
  const rows = hmData ? [["Full Name", hmData.name || "—"], ["Title / Role", hmData.title || "—"], ["Institution", hmData.school || "—"], ["In Role Since", hmData.since || "—"], ["Experience", hmData.exp ? `${hmData.exp} years` : "—"], ["Qualification", hmData.qual || "—"], ["Phone", hmData.phone || "—"], ["Email", hmData.email || "—"]] : [];
  return <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[200] animate-fade-in-scale p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-[520px] max-h-[90vh] overflow-hidden flex flex-col shadow-2xl animate-fade-in-scale" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-blue-900 via-blue-900 to-blue-900 px-6 sm:px-8 py-5 flex items-center justify-between flex-shrink-0 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-transparent"></div>
          <div className="relative z-10">
            <div className="text-white font-bold text-lg flex items-center gap-3">
              <span className="text-2xl"><Crown className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
              <span>Principal Details</span>
            </div>
            <div className="text-blue-300 text-xs mt-1">Stored profile information</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-all duration-200 text-sm backdrop-blur-sm border border-white/10 mobile-tap-target">
            <XIcon className="inline w-[1em] h-[1em] align-[-0.125em]" />
          </button>
        </div>

        {hmData && <div className="bg-gradient-to-r from-blue-50 to-blue-50 px-6 sm:px-8 py-5 flex items-center gap-5 border-b border-slate-100">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 text-white flex items-center justify-center font-bold text-xl shadow-lg shadow-blue-200">
              {initials}
            </div>
            <div>
              <div className="font-bold text-slate-800 text-lg">{hmData.name}</div>
              <div className="text-sm text-slate-500 mt-0.5">
                {hmData.title}{hmData.school ? ` · ${hmData.school}` : ""}{hmData.since ? ` · Since ${hmData.since}` : ""}
              </div>
            </div>
          </div>}

        <div className="p-6 sm:p-8 overflow-y-auto flex-1">
          {loading ? <div className="text-center py-16">
              <div className="w-10 h-10 border-3 border-slate-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
              <div className="text-slate-400 font-semibold">Loading profile...</div>
            </div> : !hmData ? <div className="text-center py-16">
              <div className="text-5xl mb-4 animate-float"><User className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
              <div className="font-semibold text-slate-500 text-sm">No principal details configured.</div>
            </div> : <>
              <div className="border border-slate-200 rounded-2xl overflow-hidden mb-6 shadow-sm">
                <table className="w-full">
                  <tbody>
                    {rows.map(([k, v], i) => <tr key={k} className={`${i % 2 === 0 ? "bg-white" : "bg-slate-50/80"} transition-colors hover:bg-blue-50/50`}>
                        <td className="px-5 py-3 text-xs font-bold text-slate-500 w-36 border-b border-slate-100 uppercase tracking-wide">{k}</td>
                        <td className="px-5 py-3 text-sm text-slate-800 font-medium border-b border-slate-100">{v}</td>
                      </tr>)}
                  </tbody>
                </table>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-blue-50 border border-blue-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="bg-blue-100/80 px-5 py-3 text-xs font-bold text-blue-700 flex items-center gap-2">
                  <span className="text-sm"><Lock className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Login Credentials
                </div>
                <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="text-[10px] text-slate-400 mb-1 uppercase tracking-wide font-bold">Username</div>
                    <div className="text-sm font-mono font-semibold text-slate-800 bg-white/60 px-3 py-2 rounded-lg border border-blue-100">{hmData.username || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-400 mb-1 uppercase tracking-wide font-bold">Password</div>
                    <div className="text-sm font-mono font-semibold text-slate-800 flex items-center gap-2 bg-white/60 px-3 py-2 rounded-lg border border-blue-100">
                      <span>{showPw ? hmData.passwordDisplay || "••••••••" : "••••••••"}</span>
                      <button onClick={() => setShowPw(p => !p)} className="text-slate-400 hover:text-slate-600 text-xs transition-colors p-1 hover:bg-slate-100 rounded">
                        {showPw ? <EyeOff className="inline w-[1em] h-[1em] align-[-0.125em]" /> : <Eye className="inline w-[1em] h-[1em] align-[-0.125em]" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>}

          <button onClick={onClose} className="w-full mt-6 py-3 bg-gradient-to-r from-blue-100 to-blue-100 text-blue-700 rounded-xl text-sm font-bold hover:from-blue-200 hover:to-blue-200 transition-all duration-200 shadow-sm mobile-tap-target">
            Close Profile
          </button>
        </div>
      </div>
    </div>;
}

// ─── STAFF ATTENDANCE PAGE ─────────────────────────────────────────────────────

function StaffAttendancePage({
  onBack
}) {
  const [staffList, setStaffList] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [attError, setAttError] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [historyStaff, setHistoryStaff] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyMonthValue, setHistoryMonthValue] = useState(""); // "YYYY-MM"

  const todayISO = new Date().toISOString().split("T")[0];
  const selDateObj = new Date(selectedDate + "T00:00:00");
  const selDayLabel = selDateObj.toLocaleDateString("en-IN", {
    weekday: "long"
  });
  const selDateLabel = selDateObj.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "long",
    year: "numeric"
  });

  // ── Load staff list once ────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch("https://schoolcrm-fzcv.onrender.com/api/staff");
        if (r.ok) {
          const data = await r.json();
          setStaffList(data);
        }
      } catch (err) {
        console.error("Staff fetch error:", err);
      }
    })();
  }, []);

  // ── This page is VIEW-ONLY: it only ever fetches the attendance staff
  // already marked for themselves (via Daily Attendance / staff-attendance
  // API) for whichever date is selected. There is no way to mark/edit
  // attendance from here — the Principal can only view it.
  useEffect(() => {
    if (staffList.length === 0) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setAttError("");
      try {
        const results = await Promise.all(staffList.map(s => fetch(`https://schoolcrm-fzcv.onrender.com/api/staff-attendance/today?date=${selectedDate}&staffId=${s._id}`).then(r => r.ok ? r.json() : []).catch(() => [])));
        if (cancelled) return;
        const map = {};
        staffList.forEach((s, i) => {
          const rec = Array.isArray(results[i]) ? results[i][0] : results[i];
          map[s._id] = rec || null;
        });
        setAttendanceMap(map);
      } catch (err) {
        if (!cancelled) setAttError("Failed to load staff attendance for this date.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [staffList, selectedDate]);
  async function openHistory(s) {
    setHistoryStaff(s);
    setHistoryLoading(true);
    setHistoryData([]);
    const now = new Date();
    setHistoryMonthValue(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
    await loadHistory(s, now.getFullYear(), now.getMonth() + 1);
  }
  async function loadHistory(s, year, month) {
    setHistoryLoading(true);
    try {
      const mm = String(month).padStart(2, "0");
      const r = await fetch(`https://schoolcrm-fzcv.onrender.com/api/staff-attendance/month?year=${year}&month=${mm}&staffId=${s._id}`);
      if (r.ok) setHistoryData(await r.json());else setHistoryData([]);
    } catch {
      setHistoryData([]);
    }
    setHistoryLoading(false);
  }
  function handleHistoryMonthChange(e) {
    const val = e.target.value; // "YYYY-MM"
    if (!val || !historyStaff) return;
    setHistoryMonthValue(val);
    const [y, m] = val.split("-").map(Number);
    loadHistory(historyStaff, y, m);
  }
  const totalStaff = staffList.length;
  const presentCount = Object.values(attendanceMap).filter(r => r && r.status === "present").length;
  const lateCount = Object.values(attendanceMap).filter(r => r && r.status === "late").length;
  const leaveCount = Object.values(attendanceMap).filter(r => r && r.status === "leave").length;
  const notMarkedCount = Object.values(attendanceMap).filter(r => !r).length;
  const filtered = staffList.filter(s => {
    const att = attendanceMap[s._id];
    const status = att ? att.status : "not_marked";
    const q = search.trim().toLowerCase();
    const matchSearch = !q || s.name && s.name.toLowerCase().includes(q) || s.dept && s.dept.toLowerCase().includes(q) || s.role && s.role.toLowerCase().includes(q) || s.phone && s.phone.toLowerCase().includes(q);
    const matchFilter = filterStatus === "all" || filterStatus === "not_marked" && !att || filterStatus === status;
    return matchSearch && matchFilter;
  });
  const AV_COLORS_MAP = [{
    bg: "bg-gradient-to-br from-blue-600 to-blue-700",
    text: "text-white"
  }, {
    bg: "bg-gradient-to-br from-blue-600 to-blue-700",
    text: "text-white"
  }, {
    bg: "bg-gradient-to-br from-blue-600 to-blue-700",
    text: "text-white"
  }, {
    bg: "bg-gradient-to-br from-blue-600 to-blue-700",
    text: "text-white"
  }, {
    bg: "bg-gradient-to-br from-blue-600 to-blue-700",
    text: "text-white"
  }, {
    bg: "bg-gradient-to-br from-blue-600 to-blue-700",
    text: "text-white"
  }, {
    bg: "bg-gradient-to-br from-blue-600 to-blue-700",
    text: "text-white"
  }, {
    bg: "bg-gradient-to-br from-blue-600 to-blue-700",
    text: "text-white"
  }];
  function getAvColor(s, i) {
    if (s.avBg && s.avColor) return {
      bg: s.avBg,
      text: s.avColor
    };
    return AV_COLORS_MAP[i % AV_COLORS_MAP.length];
  }
  function getStatusBadge(rec) {
    if (!rec) return <span className="text-[10px] font-bold px-3 py-1.5 rounded-full bg-slate-100 text-slate-400 uppercase tracking-wider border border-slate-200 shadow-sm">
        Not Marked
      </span>;
    const cfg = {
      present: "bg-blue-50 text-blue-700 border-blue-200",
      late: "bg-blue-50 text-blue-700 border-blue-200",
      leave: "bg-red-50 text-red-600 border-red-200",
      absent: "bg-rose-50 text-rose-600 border-rose-200"
    };
    const lbl = {
      present: "Present",
      late: "⏰ Late",
      leave: "Leave",
      absent: "Absent"
    };
    return <span className={`text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider border shadow-sm ${cfg[rec.status] || "bg-gray-50 text-gray-500 border-gray-200"}`}>
        {lbl[rec.status] || rec.status}
      </span>;
  }
  if (historyStaff) {
    const daysPresent = historyData.filter(r => r.status === "present" || r.status === "late").length;
    const lateDays = historyData.filter(r => r.status === "late").length;
    const leavesTaken = historyData.filter(r => r.status === "leave").length;
    const av = getAvColor(historyStaff, 0);
    const historyMonthLabel = historyMonthValue ? new Date(`${historyMonthValue}-01T00:00:00`).toLocaleDateString("en-IN", {
      month: "long",
      year: "numeric"
    }) : "";
    return <div className="animate-fade-in-up">
        <BackBtn onClick={() => setHistoryStaff(null)} label="Staff Attendance" />
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div className="flex items-center gap-5">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-bold shadow-lg ${av.bg} ${av.text}`}>
              {historyStaff.initials || (historyStaff.name || "").slice(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="text-xl font-bold text-slate-800">{historyStaff.name}</div>
              <div className="text-sm text-slate-500 mt-1">{historyStaff.role} · {historyStaff.dept}</div>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm">
            <i className="fas fa-calendar-alt text-blue-600"></i>
            <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Month</label>
            <input type="month" value={historyMonthValue} max={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`} onChange={handleHistoryMonthChange} className="text-sm font-semibold text-slate-700 outline-none bg-transparent" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
          {[{
          v: daysPresent,
          l: "Days Present",
          c: "text-blue-600",
          bg: "bg-blue-50",
          border: "border-blue-200",
          icon: <CheckCircle2 className="inline w-[1em] h-[1em] align-[-0.125em]" />
        }, {
          v: lateDays,
          l: "Late Arrivals",
          c: "text-blue-600",
          bg: "bg-blue-50",
          border: "border-blue-200",
          icon: "⏰"
        }, {
          v: leavesTaken,
          l: "Leaves Taken",
          c: "text-blue-600",
          bg: "bg-blue-50",
          border: "border-blue-200",
          icon: <ClipboardListIcon className="inline w-[1em] h-[1em] align-[-0.125em]" />
        }].map(({
          v,
          l,
          c,
          bg,
          border,
          icon
        }) => <GlassCard key={l}>
              <div className={`${bg} border ${border} rounded-2xl p-5 text-center`}>
                <div className="text-3xl mb-2">{icon}</div>
                <div className={`text-4xl font-extrabold ${c}`}><AnimatedCounter value={v} /></div>
                <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-2 font-bold">{l}</div>
              </div>
            </GlassCard>)}
        </div>

        <GlassCard>
          <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white">
            <div className="text-sm font-bold text-slate-700 flex items-center gap-2">
              <span className="text-lg"><Calendar className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Attendance History — {historyMonthLabel}
            </div>
          </div>
          {historyLoading ? <div className="py-16 text-center">
              <div className="w-8 h-8 border-3 border-slate-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-3"></div>
              <div className="text-slate-400 font-semibold">Loading history...</div>
            </div> : historyData.length === 0 ? <div className="py-16 text-center">
              <div className="text-5xl mb-4 animate-float"><Calendar className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
              <div className="text-slate-400 font-semibold">No records for {historyMonthLabel}</div>
            </div> : <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    {["Date", "Day", "Year", "Status", "Time", "Note"].map(h => <th key={h} className="text-[11px] text-slate-400 font-bold uppercase tracking-wide px-6 py-4 text-left">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {[...historyData].sort((a, b) => new Date(b.date) - new Date(a.date)).map((rec, i) => {
                const d = new Date(rec.date);
                const dayName = d.toLocaleDateString("en-IN", {
                  weekday: "long"
                });
                const dateFormatted = d.toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short"
                });
                const yearFormatted = d.toLocaleDateString("en-IN", {
                  year: "numeric"
                });
                const cfg = {
                  present: "bg-blue-50 text-blue-700 border-blue-200",
                  late: "bg-blue-50 text-blue-700 border-blue-200",
                  leave: "bg-red-50 text-red-600 border-red-200",
                  absent: "bg-rose-50 text-rose-600 border-rose-200"
                };
                const lbl = {
                  present: "Present",
                  late: "⏰ Late",
                  leave: "Leave",
                  absent: "Absent"
                };
                return <tr key={i} className="border-b border-slate-50 hover:bg-blue-50/30 transition-colors duration-200">
                        <td className="px-6 py-3.5 text-sm font-bold text-slate-700">{dateFormatted}</td>
                        <td className="px-6 py-3.5 text-sm text-slate-500">{dayName}</td>
                        <td className="px-6 py-3.5 text-sm text-slate-500">{yearFormatted}</td>
                        <td className="px-6 py-3.5">
                          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider border shadow-sm ${cfg[rec.status] || "bg-gray-50 text-gray-500 border-gray-200"}`}>
                            {lbl[rec.status] || rec.status}
                          </span>
                        </td>
                        <td className="px-6 py-3.5 text-sm text-slate-600 font-mono">{rec.time || "—"}</td>
                        <td className="px-6 py-3.5 text-xs text-slate-400 italic">{rec.note || "—"}</td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>}
        </GlassCard>
      </div>;
  }
  return <div className="animate-fade-in-up">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800">Staff Attendance — {selDayLabel}, {selDateLabel}</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">Viewing attendance staff have marked for themselves — view only</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm">
          <i className="fas fa-calendar-alt text-blue-600"></i>
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Filter by date</label>
          <input type="date" value={selectedDate} max={todayISO} onChange={e => setSelectedDate(e.target.value)} className="text-sm font-semibold text-slate-700 outline-none bg-transparent" />
        </div>
      </div>

      <div className="flex gap-3 flex-wrap mb-6 items-center">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Search className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
          <input value={search} onChange={e => setSearch(e.target.value)} className="bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-700 outline-none w-64 placeholder-slate-400 shadow-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-300 transition-all" placeholder="Search staff name, dept, role, phone..." />
          {search && <span onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 cursor-pointer text-xs">
              <XIcon className="inline w-[1em] h-[1em] align-[-0.125em]" />
            </span>}
        </div>
        {[{
        key: "all",
        label: "All"
      }, {
        key: "present",
        label: "Present"
      }, {
        key: "late",
        label: "⏰ Late"
      }, {
        key: "leave",
        label: "Leave"
      }, {
        key: "not_marked",
        label: "Not Marked"
      }].map(f => <button key={f.key} onClick={() => setFilterStatus(f.key)} className={`px-4 py-2.5 rounded-xl text-xs font-bold border transition-all duration-200 shadow-sm ${filterStatus === f.key ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white border-blue-600 shadow-blue-200" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
            {f.label}
          </button>)}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[{
        v: presentCount,
        l: "Present",
        c: "text-blue-700",
        bg: "bg-blue-50",
        border: "border-blue-200",
        icon: <CheckCircle2 className="inline w-[1em] h-[1em] align-[-0.125em]" />
      }, {
        v: lateCount,
        l: "Late",
        c: "text-blue-700",
        bg: "bg-blue-50",
        border: "border-blue-200",
        icon: "⏰"
      }, {
        v: leaveCount,
        l: "On Leave",
        c: "text-blue-700",
        bg: "bg-blue-50",
        border: "border-blue-200",
        icon: <ClipboardListIcon className="inline w-[1em] h-[1em] align-[-0.125em]" />
      }, {
        v: notMarkedCount,
        l: "Not Marked",
        c: "text-slate-500",
        bg: "bg-slate-50",
        border: "border-slate-200",
        icon: <HelpCircle className="inline w-[1em] h-[1em] align-[-0.125em]" />
      }].map(({
        v,
        l,
        c,
        bg,
        border,
        icon
      }) => <GlassCard key={l}>
            <div className={`${bg} border ${border} rounded-2xl p-5 text-center`}>
              <div className="text-2xl mb-2 animate-float">{icon}</div>
              <div className={`text-3xl font-extrabold ${c}`}><AnimatedCounter value={v} /></div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wide mt-2 font-bold">{l}</div>
            </div>
          </GlassCard>)}
      </div>

      {attError && <div className="mb-6 bg-red-50 border border-red-200 text-red-600 rounded-xl px-6 py-4 text-sm font-bold">
          <i className="fas fa-exclamation-triangle mr-2"></i>{attError}
        </div>}

      {loading ? <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-slate-100 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
            <div className="text-slate-500 font-bold text-lg animate-pulse">Loading Staff Attendance...</div>
          </div>
        </div> : <GlassCard>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white">
                  {["#", "Staff", "Department", "Role", "Check-in Time", "Status", "Note", "History"].map(h => <th key={h} className="text-[11px] text-slate-500 font-bold uppercase tracking-wide px-5 py-4 text-left">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? <tr>
                    <td colSpan={8} className="py-20 text-center">
                      <div className="text-5xl mb-4 animate-float"><User className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
                      <div className="text-slate-400 font-semibold text-sm">No staff found</div>
                    </td>
                  </tr> : filtered.map((s, i) => {
              const av = getAvColor(s, i);
              const rec = attendanceMap[s._id];
              const initials = s.initials || (s.name || "").slice(0, 2).toUpperCase();
              return <tr key={s._id || i} className="border-b border-slate-50 hover:bg-blue-50/30 transition-colors duration-200">
                      <td className="px-5 py-4 text-sm text-slate-400 font-medium">{i + 1}</td>

                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold shadow-md ${av.bg} ${av.text}`}>
                            {initials}
                          </div>
                          <div>
                            <div className="text-sm font-bold text-slate-800">{s.name}</div>
                            <div className="text-[11px] text-slate-400 mt-0.5">{s.phone || "—"}</div>
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4 text-sm text-slate-600 font-medium">{s.dept || "—"}</td>
                      <td className="px-5 py-4 text-sm text-slate-500">{s.role || "—"}</td>

                      <td className="px-5 py-4">
                        {rec ? <span className="font-mono text-sm font-bold text-slate-700 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200">{rec.time || "—"}</span> : <span className="text-slate-300 text-sm">—</span>}
                      </td>

                      <td className="px-5 py-4">{getStatusBadge(rec)}</td>

                      <td className="px-5 py-4 text-xs text-slate-400 italic max-w-[120px] truncate">
                        {rec?.note || "—"}
                      </td>

                      <td className="px-5 py-4">
                        <button onClick={() => openHistory(s)} className="text-[11px] font-bold px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 border border-blue-200 rounded-xl hover:from-blue-100 hover:to-blue-200 transition-all duration-200 shadow-sm">
                          <BarChart3 className="inline w-[1em] h-[1em] align-[-0.125em]" /> History
                        </button>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>
        </GlassCard>}

      {!loading && totalStaff > 0 && <div className="mt-6 flex flex-col md:flex-row gap-4 flex-wrap">
          <GlassCard hover={false} className="flex-1">
            <div className="px-6 py-4">
              <div className="text-xs text-slate-400 uppercase tracking-wide mb-3 font-bold">Attendance Overview</div>
              <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden flex shadow-inner">
                <div className="h-full bg-gradient-to-r from-blue-400 to-blue-500 transition-all duration-700" style={{
              width: `${totalStaff > 0 ? presentCount / totalStaff * 100 : 0}%`
            }}></div>
                <div className="h-full bg-gradient-to-r from-blue-400 to-blue-400 transition-all duration-700" style={{
              width: `${totalStaff > 0 ? lateCount / totalStaff * 100 : 0}%`
            }}></div>
                <div className="h-full bg-gradient-to-r from-blue-400 to-blue-500 transition-all duration-700" style={{
              width: `${totalStaff > 0 ? leaveCount / totalStaff * 100 : 0}%`
            }}></div>
              </div>
              <div className="flex gap-6 mt-3 text-[11px] font-bold flex-wrap">
                <span className="text-blue-600 flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span> Present ({presentCount})
                </span>
                <span className="text-blue-600 flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span> Late ({lateCount})
                </span>
                <span className="text-blue-500 flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span> Leave ({leaveCount})
                </span>
                <span className="text-slate-400 flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-300"></span> Not Marked ({notMarkedCount})
                </span>
              </div>
            </div>
          </GlassCard>

          <GlassCard hover={false}>
            <div className="px-6 py-4 text-center min-w-[160px]">
              <div className="text-xs text-slate-400 uppercase tracking-wide mb-2 font-bold">Attendance Rate</div>
              <div className="text-3xl font-extrabold text-blue-600">
                {totalStaff > 0 ? Math.round((presentCount + lateCount) / totalStaff * 100) : 0}%
              </div>
            </div>
          </GlassCard>
        </div>}
    </div>;
}

// ─── DONATIONS PAGE ──────────────────────────────────────────────────────────

function DonationsPage({
  onBack
}) {
  const schoolName = useSchoolName();
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [directionFilter, setDirectionFilter] = useState("all");

  // Pulls every donation record straight from MongoDB (both directions —
  // donations RECEIVED BY the school, and donations GIVEN BY the school to
  // someone else). No mock/fallback data: if the fetch fails, we show a
  // clear error instead of silently substituting fake rows.
  const fetchDonations = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch('https://schoolcrm-fzcv.onrender.com/api/donations');
      if (!res.ok) throw new Error(`Server responded with ${res.status}`);
      const data = await res.json();
      setDonations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching donations:", err);
      setError(" Could not load donations. Is the backend running on port 5000?");
    }
    setLoading(false);
  };
  useEffect(() => {
    fetchDonations();
  }, []);

  // ── Derived stats — computed fresh from the real fetched records ────────
  const totalDonations = donations.length;
  const totalAmount = donations.reduce((sum, d) => sum + (d.amount || 0), 0);
  const totalReceived = donations.reduce((sum, d) => sum + (d.amountReceived || 0), 0);
  const totalPending = donations.reduce((sum, d) => sum + Math.max(0, (d.amount || 0) - (d.amountReceived || 0)), 0);
  const averageAmount = totalDonations > 0 ? Math.round(totalAmount / totalDonations) : 0;
  // Unique donors — keyed by phone/email/name (whichever is available) so two
  // truly anonymous donations with no contact info still count separately.
  const totalDonors = new Set(donations.map((d, i) => d.partyPhone || d.partyEmail || (d.anonymous ? `anon-${d._id || i}` : d.partyName) || `unknown-${i}`)).size;
  const statusMeta = {
    pending: {
      label: "Pending",
      icon: "⏳",
      classes: "bg-blue-50 text-blue-700 border-blue-200"
    },
    completed: {
      label: "Completed",
      icon: <CheckCircle2 className="inline w-[1em] h-[1em] align-[-0.125em]" />,
      classes: "bg-blue-50 text-blue-700 border-blue-200"
    },
    received: {
      label: "Received",
      icon: <Mail className="inline w-[1em] h-[1em] align-[-0.125em]" />,
      classes: "bg-blue-50 text-blue-700 border-blue-200"
    },
    approved: {
      label: "Approved",
      icon: <ThumbsUp className="inline w-[1em] h-[1em] align-[-0.125em]" />,
      classes: "bg-blue-50 text-blue-700 border-blue-200"
    },
    cancelled: {
      label: "Cancelled",
      icon: <XCircle className="inline w-[1em] h-[1em] align-[-0.125em]" />,
      classes: "bg-red-50 text-red-600 border-red-200"
    }
  };
  const filteredDonations = donations.filter(d => {
    const q = search.trim().toLowerCase();
    const matchSearch = !q || [d.partyName, d.partyEmail, d.partyPhone, d.purpose, d.purposeDetails, d.studentName, d.studentClass, d.trustName, d.receiptNumber, d.notes].some(v => (v || "").toString().toLowerCase().includes(q));
    const matchStatus = statusFilter === "all" || d.status === statusFilter;
    const matchDirection = directionFilter === "all" || d.direction === directionFilter;
    return matchSearch && matchStatus && matchDirection;
  });
  const formatCurrency = amount => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };
  return <div className="animate-fade-in-up">
      <BackBtn onClick={onBack} label="Dashboard" />
      
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800 flex items-center gap-2">
            <Gift size={28} className="text-blue-600" />
            Donations Management
          </h2>
          <p className="text-sm text-slate-400 mt-1">Donations received by the school, and donations given by the school — tracked in real-time</p>
        </div>
        <button onClick={fetchDonations} className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-bold rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 mobile-tap-target">
          <RefreshCw className="inline w-[1em] h-[1em] align-[-0.125em]" /> Refresh
        </button>
      </div>

      {error && <div className="mb-6 bg-red-50 border border-red-200 text-red-600 rounded-xl px-5 py-3.5 text-sm font-semibold">
          {error}
        </div>}

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        {[{
        label: "Total Donations",
        value: totalDonations,
        icon: <Package className="inline w-[1em] h-[1em] align-[-0.125em]" />
      }, {
        label: "Total Amount",
        value: formatCurrency(totalAmount),
        icon: <CoinsIcon className="inline w-[1em] h-[1em] align-[-0.125em]" />
      }, {
        label: "Amount Received",
        value: formatCurrency(totalReceived),
        icon: <CheckCircle2 className="inline w-[1em] h-[1em] align-[-0.125em]" />
      }, {
        label: "Amount Pending",
        value: formatCurrency(totalPending),
        icon: "⏳"
      }, {
        label: "Total Donors",
        value: totalDonors,
        icon: <User className="inline w-[1em] h-[1em] align-[-0.125em]" />
      }, {
        label: "Average Donation",
        value: formatCurrency(averageAmount),
        icon: <BarChart3 className="inline w-[1em] h-[1em] align-[-0.125em]" />
      }].map((stat, i) => <GlassCard key={i}>
            <div className="bg-blue-50 rounded-2xl p-5 text-center border border-slate-100">
              <div className="text-2xl mb-2">{stat.icon}</div>
              <div className="text-lg sm:text-xl font-extrabold text-blue-600">{stat.value}</div>
              <div className="text-[11px] text-slate-400 mt-1 font-bold">{stat.label}</div>
            </div>
          </GlassCard>)}
      </div>

      <div className="flex flex-col gap-3 mb-6">
        <div className="flex gap-3 flex-wrap items-center">
          <div className="relative flex-1 max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Search className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
            <input value={search} onChange={e => setSearch(e.target.value)} className="bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-700 outline-none w-full placeholder-slate-400 shadow-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-300 transition-all" placeholder="Search donor, phone, email, purpose, receipt no..." />
          </div>
          {[{
          key: "all",
          label: "All Status"
        }, {
          key: "pending",
          label: "⏳ Pending"
        }, {
          key: "completed",
          label: "Completed"
        }, {
          key: "received",
          label: "Received"
        }, {
          key: "approved",
          label: "Approved"
        }, {
          key: "cancelled",
          label: "Cancelled"
        }].map(f => <button key={f.key} onClick={() => setStatusFilter(f.key)} className={`px-4 py-2.5 rounded-xl text-xs font-bold border transition-all duration-200 shadow-sm ${statusFilter === f.key ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white border-blue-600 shadow-blue-200" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
              {f.label}
            </button>)}
        </div>

        <div className="flex gap-3 flex-wrap items-center">
          <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">Type:</span>
          {[{
          key: "all",
          label: "All"
        }, {
          key: "received",
          label: "Received by School"
        }, {
          key: "given",
          label: "Given by School"
        }].map(f => <button key={f.key} onClick={() => setDirectionFilter(f.key)} className={`px-4 py-2.5 rounded-xl text-xs font-bold border transition-all duration-200 shadow-sm ${directionFilter === f.key ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white border-blue-600 shadow-blue-200" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
              {f.label}
            </button>)}
        </div>
      </div>

      {loading ? <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-slate-100 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
            <div className="text-slate-500 font-bold text-lg animate-pulse">Loading Donations...</div>
          </div>
        </div> : <GlassCard>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white">
                  {["#", "Donor Details", "Contact", "Amount", "Received", "Pending", "Purpose", "Date", "Type", "Status"].map(h => <th key={h} className="text-[11px] text-slate-500 font-bold uppercase tracking-wide px-5 py-4 text-left whitespace-nowrap">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {filteredDonations.length === 0 ? <tr>
                    <td colSpan={10} className="py-20 text-center">
                      <div className="text-5xl mb-4 animate-float"><GiftIcon className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
                      <div className="text-slate-400 font-semibold text-sm">
                        {donations.length === 0 ? "No donations recorded yet" : "No donations match your search/filters"}
                      </div>
                    </td>
                  </tr> : filteredDonations.map((d, i) => {
              const isAnon = !!d.anonymous;
              const displayName = isAnon ? "Anonymous" : d.partyName || "Unnamed Donor";
              const received = d.amountReceived || 0;
              const pending = Math.max(0, (d.amount || 0) - received);
              const dateVal = d.donationDate || d.createdAt;
              const meta = statusMeta[d.status] || {
                label: d.status || "—",
                icon: "•",
                classes: "bg-slate-50 text-slate-500 border-slate-200"
              };
              return <tr key={d._id || i} className="border-b border-slate-50 hover:bg-blue-50/30 transition-colors duration-200">
                        <td className="px-5 py-4 text-sm text-slate-400 font-medium">{i + 1}</td>
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold shadow-md bg-gradient-to-br from-blue-600 to-blue-700 text-white shrink-0">
                              {isAnon ? '?' : displayName.charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-bold text-slate-800 truncate">{displayName}</div>
                              <div className="text-[11px] text-slate-400 truncate">
                                {isAnon ? '—' : d.partyEmail || '—'}
                                {d.studentName ? ` · ${d.studentName}${d.studentClass ? ` (${d.studentClass})` : ''}` : ''}
                                {d.trustName ? ` · ${d.trustName}` : ''}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-600 font-medium whitespace-nowrap">
                          {isAnon ? '—' : d.partyPhone || '—'}
                        </td>
                        <td className="px-5 py-4 text-sm font-bold text-slate-800 whitespace-nowrap">
                          {formatCurrency(d.amount)}
                        </td>
                        <td className="px-5 py-4 text-sm font-bold text-blue-600 whitespace-nowrap">
                          {formatCurrency(received)}
                        </td>
                        <td className={`px-5 py-4 text-sm font-bold whitespace-nowrap ${pending > 0 ? "text-red-500" : "text-slate-400"}`}>
                          {pending > 0 ? formatCurrency(pending) : '—'}
                        </td>
                        <td className="px-5 py-4 text-sm text-slate-600 capitalize">{(d.purpose || '—').toString().replace(/_/g, ' ')}</td>
                        <td className="px-5 py-4 text-sm text-slate-500 whitespace-nowrap">
                          {dateVal ? new Date(dateVal).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  }) : '—'}
                        </td>
                        <td className="px-5 py-4">
                          <span className={`text-[10px] font-bold px-2.5 py-1.5 rounded-full uppercase tracking-wider border shadow-sm whitespace-nowrap ${d.direction === 'given' ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-blue-50 text-blue-600 border-blue-100"}`}>
                            {d.direction === 'given' ? "Given" : "Received"}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <span className={`text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider border shadow-sm whitespace-nowrap ${meta.classes}`}>
                            {meta.icon} {meta.label}
                          </span>
                        </td>
                      </tr>;
            })}
              </tbody>
            </table>
          </div>
        </GlassCard>}

      <div className="mt-8 text-center text-xs text-slate-400 border-t border-slate-200 pt-6">
        All donations are tracked in real-time · {schoolName} School Management System
      </div>
    </div>;
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────

function Dashboard({
  navigate
}) {
  const schoolName = useSchoolName();
  const [loaded, setLoaded] = useState(false);
  const [dashStaff, setDashStaff] = useState({
    total: 0,
    present: 0
  });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [markStats, setMarkStats] = useState({
    totalStudents: 0,
    averagePercentage: 0,
    topPerformers: 0,
    needsImprovement: 0
  });

  // ── Enrollment by Grade / Enrollment by Fees toggle ─────────────────────
  const [enrollView, setEnrollView] = useState("grade"); // 'grade' | 'fees'
  const [feesSelectedClass, setFeesSelectedClass] = useState(null); // class drilldown (null = class list)
  const [feesActiveTerm, setFeesActiveTerm] = useState("T1"); // 'T1' | 'T2' | 'T3'
  const [feesTermData, setFeesTermData] = useState({}); // { [cls]: { T1:{due,paid,pending}, T2:{...}, T3:{...} } }
  const [feesLoadingClass, setFeesLoadingClass] = useState(false);

  // Pulls each student's per-term fee breakdown (from the same FeeCollection
  // record the Fee Ledger reads/writes) and sums it up class-wide, so T1/T2/T3
  // reflects exactly what's been collected for that class.
  async function fetchClassFeeTermData(cls) {
    const students = getStudents(cls);
    const results = await Promise.all(students.map(s => fetch(`https://schoolcrm-fzcv.onrender.com/api/fees/full/${s._id}`).then(r => r.ok ? r.json() : null).catch(() => null)));
    const agg = {
      T1: {
        due: 0,
        paid: 0,
        pending: 0
      },
      T2: {
        due: 0,
        paid: 0,
        pending: 0
      },
      T3: {
        due: 0,
        paid: 0,
        pending: 0
      }
    };
    results.forEach(r => {
      if (!r || !r.termFees) return;
      ["T1", "T2", "T3"].forEach(t => {
        const tf = r.termFees[t];
        if (!tf) return;
        agg[t].due += tf.due || 0;
        agg[t].paid += tf.paid || 0;
        agg[t].pending += tf.pending || 0;
      });
    });
    return agg;
  }
  async function openClassFees(cls) {
    setFeesSelectedClass(cls);
    setFeesActiveTerm("T1");
    if (feesTermData[cls]) return; // already cached, no need to refetch
    setFeesLoadingClass(true);
    try {
      const data = await fetchClassFeeTermData(cls);
      setFeesTermData(prev => ({
        ...prev,
        [cls]: data
      }));
    } catch (e) {
      /* keep whatever was cached */
    } finally {
      setFeesLoadingClass(false);
    }
  }
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('https://schoolcrm-fzcv.onrender.com/api/staff');
        if (r.ok) {
          const data = await r.json();
          setDashStaff({
            total: data.length,
            present: data.filter(s => s.status === 'present').length
          });
        }
      } catch {}
    })();
    (async () => {
      try {
        let totalMarks = 0;
        let totalStudents = 0;
        let topPerformers = 0;
        let needsImprovement = 0;
        let subjectCount = 0;
        for (const cls of ['Class 10', 'Class 11', 'Class 12']) {
          const students = getStudents(cls);
          for (const student of students) {
            try {
              const res = await fetch(`https://schoolcrm-fzcv.onrender.com/api/marks/student/${student._id}?className=${encodeURIComponent(cls)}`);
              if (res.ok) {
                const data = await res.json();
                let studentTotal = 0;
                let studentSubjects = 0;
                data.forEach(doc => {
                  if (doc.entry && doc.entry.marks !== null && doc.entry.marks !== undefined) {
                    const pct = doc.entry.marks / (doc.entry.maxMarks || 100) * 100;
                    studentTotal += pct;
                    studentSubjects++;
                  }
                });
                if (studentSubjects > 0) {
                  const avg = studentTotal / studentSubjects;
                  totalMarks += avg;
                  totalStudents++;
                  if (avg >= 80) topPerformers++;
                  if (avg < 60) needsImprovement++;
                  subjectCount += studentSubjects;
                }
              }
            } catch (e) {}
          }
        }
        setMarkStats({
          totalStudents,
          averagePercentage: totalStudents > 0 ? Math.round(totalMarks / totalStudents) : 0,
          topPerformers,
          needsImprovement
        });
      } catch (e) {
        console.error("Error fetching mark stats:", e);
      }
    })();
    if (admissionsLoaded) {
      setLoaded(true);
      return;
    }
    (async () => {
      try {
        let res = await fetch('/api/admissions');
        let triedFallback = false;
        if (!res.ok) {
          res = await fetch('https://schoolcrm-fzcv.onrender.com/api/admissions');
          triedFallback = true;
        }
        if (!res.ok) throw new Error('Failed to fetch admissions');
        const ct = (res.headers.get('content-type') || '').toLowerCase();
        let data;
        if (!ct.includes('application/json')) {
          if (!triedFallback) {
            res = await fetch('https://schoolcrm-fzcv.onrender.com/api/admissions');
            if (!res.ok) throw new Error('Failed to fetch admissions (fallback)');
          } else {
            throw new Error('Admissions endpoint did not return JSON (got ' + ct + ')');
          }
        }
        data = await res.json();
        populateStudentCacheFromAdmissions(data);
        admissionsLoaded = true;
        setLoaded(true);
      } catch (err) {
        console.error('Failed to load admissions for dashboard:', err);
        setLoaded(true);
      }
    })();
  }, []);
  const formatDate = date => {
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Asia/Kolkata'
    };
    return date.toLocaleDateString('en-IN', options);
  };
  const formatTime = date => {
    const options = {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    };
    return date.toLocaleTimeString('en-IN', options);
  };
  const formatMonthYear = date => {
    const options = {
      month: 'long',
      year: 'numeric',
      timeZone: 'Asia/Kolkata'
    };
    return date.toLocaleDateString('en-IN', options);
  };
  if (!loaded) return <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-slate-100 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
        <div className="text-slate-500 font-bold text-lg animate-pulse">Loading Dashboard Data...</div>
      </div>
    </div>;
  const allStudents = Object.values(studentCache).flat();
  const totalStudents = allStudents.length;
  const totalAdmitted = allStudents.filter(s => s.admissionStatus === 'admitted' || s.admissionStatus === 'joined').length;
  const classDist = Object.keys(studentCache).map(cls => ({
    cls: cls.replace('Class ', 'C').replace('UKG', 'UKG').replace('LKG', 'LKG'),
    count: studentCache[cls].length
  }));
  const maxCount = Math.max(...classDist.map(d => d.count), 1);
  const hsChartData = ["Class 10", "Class 11", "Class 12"].map(cls => ({
    label: cls,
    count: studentCache[cls]?.length || 0
  }));
  const totalStaff = dashStaff.total;
  const presentStaff = dashStaff.present;
  const staffAttendancePct = totalStaff > 0 ? Math.round(presentStaff / totalStaff * 100) : 0;
  return <div className="w-full h-full min-h-screen animate-fade-in-up relative">
      {/* Animated Background Circles */}
      <AnimatedBackgroundCircles />
      
      <div className="relative z-10">
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight flex items-center gap-2 flex-wrap">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-blue-700 grow-underline">{schoolName}</span> Principal Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-1">Real-time indicators, student metrics, and administrative quick actions</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          <div className="lg:col-span-2 space-y-6">
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-6 shadow-xl shadow-blue-500/10 hover:shadow-2xl transition-all duration-300 relative overflow-hidden group text-white shine-hover">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16 blur-2xl group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full translate-y-12 -translate-x-12 blur-xl"></div>
                <div className="flex items-center justify-between mb-4 relative z-10">
                  <span className="text-sm font-bold text-blue-100 uppercase tracking-wider">Total Students</span>
                  <span className="text-2xl"><User className="inline w-[1em] h-[1em] align-[-0.125em]" /><GraduationCapIcon className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                </div>
                <div className="flex items-baseline gap-2 relative z-10">
                  <span className="text-4xl font-extrabold text-white tracking-tight">
                    <AnimatedCounter value={totalStudents} />
                  </span>
                  <span className="text-xs font-bold text-white bg-white/20 px-2.5 py-1 rounded-full backdrop-blur-sm border border-white/10">
                    +{totalAdmitted} joined
                  </span>
                </div>
                <div className="w-full bg-white/20 h-1.5 rounded-full mt-4 overflow-hidden relative z-10">
                  <div className="bg-white h-full rounded-full transition-all duration-1000" style={{
                  width: `${Math.min(100, totalStudents / 200 * 100)}%`
                }}></div>
                </div>
              </div>

              <div onClick={() => navigate('donations')} className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-6 shadow-xl shadow-blue-500/10 hover:shadow-2xl transition-all duration-300 relative overflow-hidden group text-white cursor-pointer shine-hover">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16 blur-2xl group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full translate-y-12 -translate-x-12 blur-xl"></div>
                <div className="flex items-center justify-between mb-4 relative z-10">
                  <span className="text-sm font-bold text-blue-100 uppercase tracking-wider">Donations</span>
                  <span className="text-2xl"><GiftIcon className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                </div>
                <div className="flex items-baseline gap-2 relative z-10">
                  <span className="text-4xl font-extrabold text-white tracking-tight">
                    ₹2.85L
                  </span>
                  <span className="text-xs font-bold text-white bg-white/20 px-2.5 py-1 rounded-full backdrop-blur-sm border border-white/10">
                    12 donors
                  </span>
                </div>
                <div className="w-full bg-white/20 h-1.5 rounded-full mt-4 overflow-hidden relative z-10">
                  <div className="bg-white h-full rounded-full transition-all duration-1000" style={{
                  width: '72%'
                }}></div>
                </div>
                <div className="mt-3 text-xs text-blue-200 flex items-center gap-1 relative z-10">
                  Click to view donations <ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200/60 rounded-2xl p-5 sm:p-6 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 mb-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">
                    {enrollView === "grade" ? "Enrollment by Grade" : "Enrollment by Fees"}
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {enrollView === "grade" ? "Real-time student headcount per class level" : "Select a class, then a term to view fee collection"}
                  </p>
                </div>
                {enrollView === "grade" ? <div className="flex items-center gap-2 self-start sm:self-auto">
                    <span className="text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 px-3.5 py-1.5 rounded-xl shadow-sm">
                      Total Classes: {allClasses.length}
                    </span>
                    <button onClick={() => setEnrollView("fees")} className="text-xs font-bold bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3.5 py-1.5 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 flex items-center gap-1.5">
                      Enrolled by Fees <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                    </button>
                  </div> : <button onClick={() => setEnrollView("grade")} className="text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100 px-3.5 py-1.5 rounded-xl shadow-sm hover:bg-blue-100 transition-all duration-200 flex items-center gap-1.5 self-start sm:self-auto">
                    <span><ArrowLeft className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Back to Grade View
                  </button>}
              </div>

              {enrollView === "grade" ? <div className="space-y-4">
                  {classDist.map((d, i) => {
                const color = CLASS_COLORS[i % CLASS_COLORS.length];
                const pct = Math.round(d.count / maxCount * 100) || 5;
                return <div key={d.cls} className="flex items-center gap-4">
                        <span className="text-xs font-bold text-slate-500 w-12 shrink-0">{d.cls}</span>
                        <div className="flex-1 bg-slate-50 border border-slate-100 h-7 rounded-xl relative overflow-hidden">
                          <div className={`h-full rounded-xl bg-gradient-to-r ${color.gradient} opacity-85 transition-all duration-1000`} style={{
                      width: `${pct}%`
                    }}></div>
                          <span className="absolute inset-y-0 left-3 flex items-center text-xs font-bold text-slate-700">
                            {d.count} Students
                          </span>
                        </div>
                      </div>;
              })}
                </div> : <div className="flex flex-col md:flex-row gap-5">
                  <div className="md:w-40 shrink-0">
                    <div className="flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-2 md:pb-0">
                      {feesClassOrder.map(cls => {
                    const isSel = feesSelectedClass === cls;
                    return <button key={cls} onClick={() => openClassFees(cls)} className={`text-left px-3.5 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all duration-200 shrink-0 ${isSel ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md" : "bg-white text-slate-600 border border-slate-200 hover:border-blue-300 hover:bg-blue-50"}`}>
                            {cls}
                          </button>;
                  })}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0">
                    {!feesSelectedClass ? <div className="flex items-center justify-center h-56 text-sm text-slate-400 font-medium text-center px-4">
                        <ArrowLeft className="inline w-[1em] h-[1em] align-[-0.125em]" /> Select a class to view its fee collection
                      </div> : feesLoadingClass && !feesTermData[feesSelectedClass] ? <div className="flex items-center justify-center h-56">
                        <div className="w-8 h-8 border-3 border-slate-200 border-t-blue-500 rounded-full animate-spin"></div>
                      </div> : (() => {
                  const clsData = feesTermData[feesSelectedClass] || {
                    T1: {
                      due: 0,
                      paid: 0,
                      pending: 0
                    },
                    T2: {
                      due: 0,
                      paid: 0,
                      pending: 0
                    },
                    T3: {
                      due: 0,
                      paid: 0,
                      pending: 0
                    }
                  };
                  const totalDue = clsData.T1.due + clsData.T2.due + clsData.T3.due;
                  const totalPaid = clsData.T1.paid + clsData.T2.paid + clsData.T3.paid;
                  const overallPct = totalDue > 0 ? Math.round(totalPaid / totalDue * 100) : 0;
                  const term = clsData[feesActiveTerm] || {
                    due: 0,
                    paid: 0,
                    pending: 0
                  };
                  const termPct = term.due > 0 ? Math.round(term.paid / term.due * 100) : 0;
                  const termPending = Math.max(0, term.due - term.paid);
                  return <>
                            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                              <div>
                                <div className="text-sm font-bold text-slate-800">{feesSelectedClass}</div>
                                <div className="text-[11px] text-slate-400 mt-0.5">
                                  {getStudents(feesSelectedClass).length} students
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-extrabold text-blue-600">{overallPct}%</div>
                                <div className="text-[10px] text-slate-400 uppercase tracking-wide font-bold">
                                  Total Fee Collected
                                </div>
                              </div>
                            </div>

                            <div className="flex gap-2 mb-6">
                              {["T1", "T2", "T3"].map(t => <button key={t} onClick={() => setFeesActiveTerm(t)} className={`px-4 py-2 rounded-xl text-xs font-bold border transition-all duration-200 ${feesActiveTerm === t ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white border-blue-600 shadow-sm" : "bg-white text-slate-500 border-slate-200 hover:border-blue-300"}`}>
                                  {t}
                                </button>)}
                            </div>

                            <FeesPieChart paid={term.paid} pending={termPending} percent={termPct} />

                            <div className="grid grid-cols-3 gap-3 mt-6">
                              <div className="bg-white border border-slate-100 rounded-xl p-3 text-center">
                                <div className="text-sm font-extrabold text-slate-700">₹{term.due.toLocaleString()}</div>
                                <div className="text-[10px] text-slate-400 font-bold uppercase mt-1">{feesActiveTerm} Due</div>
                              </div>
                              <div className="bg-white border border-slate-100 rounded-xl p-3 text-center">
                                <div className="text-sm font-extrabold text-blue-600">₹{term.paid.toLocaleString()}</div>
                                <div className="text-[10px] text-slate-400 font-bold uppercase mt-1">{feesActiveTerm} Paid</div>
                              </div>
                              <div className="bg-white border border-slate-100 rounded-xl p-3 text-center">
                                <div className="text-sm font-extrabold text-red-500">₹{termPending.toLocaleString()}</div>
                                <div className="text-[10px] text-slate-400 font-bold uppercase mt-1">{feesActiveTerm} Pending</div>
                              </div>
                            </div>
                          </>;
                })()}
                  </div>
                </div>}
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200/60 rounded-2xl p-5 sm:p-6 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 mb-6">
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Higher Secondary Overview</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Student distribution across classes 10, 11 & 12</p>
                </div>
              </div>
              <CircularProgressChart data={hsChartData} />
              
              <div className="grid grid-cols-3 gap-3 sm:gap-4 mt-6 pt-6 border-t border-blue-200/40">
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-extrabold text-blue-600">
                    <AnimatedCounter value={markStats.topPerformers} />
                  </div>
                  <div className="text-[9px] sm:text-[10px] text-slate-500 font-bold uppercase tracking-wide mt-1">Top Performers</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-extrabold text-blue-600">
                    <AnimatedCounter value={markStats.averagePercentage} suffix="%" />
                  </div>
                  <div className="text-[9px] sm:text-[10px] text-slate-500 font-bold uppercase tracking-wide mt-1">Avg Score</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-extrabold text-blue-600">
                    <AnimatedCounter value={markStats.needsImprovement} />
                  </div>
                  <div className="text-[9px] sm:text-[10px] text-slate-500 font-bold uppercase tracking-wide mt-1">Needs Support</div>
                </div>
              </div>
            </div>

          </div>

          <div className="space-y-6">
            
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-6 shadow-xl shadow-blue-500/10 hover:shadow-2xl transition-all duration-300 relative overflow-hidden group text-white">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16 blur-2xl group-hover:scale-110 transition-transform duration-500"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full translate-y-12 -translate-x-12 blur-xl"></div>
              <div className="flex justify-between items-center mb-5 relative z-10">
                <h3 className="text-base font-bold text-white">Today's Attendance</h3>
                <span className="text-[10px] font-bold bg-white/20 text-white px-2.5 py-1 rounded-md uppercase border border-white/10 backdrop-blur-sm">DAILY RECORD</span>
              </div>
              
              <div className="space-y-5 relative z-10">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-semibold text-blue-100">Students Present</span>
                    <span className="text-sm font-extrabold text-white">94.2%</span>
                  </div>
                  <div className="w-full bg-white/20 h-2 rounded-full overflow-hidden">
                    <div className="bg-white h-full rounded-full" style={{
                    width: '94.2%'
                  }}></div>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-semibold text-blue-100">Staff Availability ({presentStaff}/{totalStaff})</span>
                    <span className="text-sm font-extrabold text-white">{staffAttendancePct}%</span>
                  </div>
                  <div className="w-full bg-white/20 h-2 rounded-full overflow-hidden">
                    <div className="bg-white h-full rounded-full" style={{
                    width: `${staffAttendancePct}%`
                  }}></div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200/60 rounded-2xl p-6 shadow-md hover:shadow-lg transition-all duration-300">
              <h3 className="text-base font-bold text-slate-900 mb-4">Quick Management Actions</h3>
              <div className="grid grid-cols-2 gap-3">
                {[{
                icon: Megaphone,
                label: "Circulars",
                page: "circular",
                bg: "bg-blue-50/80 text-blue-700 hover:bg-blue-100 border border-blue-100/50"
              }, {
                icon: CheckSquare,
                label: "Attendance",
                page: "attendance",
                bg: "bg-blue-50/80 text-blue-700 hover:bg-blue-100 border border-blue-100/50"
              }, {
                icon: CalendarDays,
                label: "Timetables",
                page: "timetable",
                bg: "bg-blue-50/80 text-blue-700 hover:bg-blue-100 border border-blue-100/50"
              }, {
                icon: Gift,
                label: "Donations",
                page: "donations",
                bg: "bg-blue-50/80 text-blue-700 hover:bg-blue-100 border border-blue-100/50"
              }, {
                icon: Users,
                label: "Directory",
                page: "students",
                bg: "bg-blue-50/80 text-blue-700 hover:bg-blue-100 border border-blue-100/50"
              }, {
                icon: GraduationCap,
                label: "Staff",
                page: "staff",
                bg: "bg-blue-50/80 text-blue-700 hover:bg-blue-100 border border-blue-100/50"
              }].map((act, i) => <div key={i} onClick={() => navigate(act.page)} className={`flex flex-col items-center justify-center p-4 rounded-xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 mobile-tap-target ${act.bg}`}>
                    <act.icon size={20} strokeWidth={2.3} className="mb-2" />
                    <span className="text-xs font-bold">{act.label}</span>
                  </div>)}
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 via-blue-50 to-blue-50/50 border border-blue-200/60 rounded-2xl p-6 shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <span className="text-lg"><Clock className="inline w-[1em] h-[1em] align-[-0.125em]" /></span> Live Clock
                </h3>
                <span className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 border border-blue-200 text-blue-700 text-[9px] font-bold rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></span>
                  LIVE
                </span>
              </div>
              
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 border border-white/50 shadow-inner">
                <div className="text-center">
                  <div className="font-mono font-bold text-3xl sm:text-4xl text-slate-800 tracking-wider">
                    {formatTime(currentTime)}
                  </div>
                  <div className="mt-2 flex items-center justify-center gap-3">
                    <span className="text-xs font-medium text-slate-500 bg-blue-100 px-3 py-1 rounded-full border border-blue-200">
                      IST
                    </span>
                    <span className="text-xs font-medium text-slate-400">•</span>
                    <span className="text-xs font-medium text-slate-500">
                      {currentTime.toLocaleDateString('en-IN', {
                      weekday: 'short'
                    })}
                    </span>
                  </div>
                </div>

                <div className="my-4 border-t border-blue-200/50"></div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-gradient-to-br from-blue-100/70 to-blue-100/70 rounded-xl p-3 text-center">
                    <div className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Date</div>
                    <div className="text-sm font-bold text-slate-700 mt-1">
                      {currentTime.toLocaleDateString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric'
                    })}
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-100/70 to-blue-100/70 rounded-xl p-3 text-center">
                    <div className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Month & Year</div>
                    <div className="text-sm font-bold text-slate-700 mt-1">
                      {formatMonthYear(currentTime)}
                    </div>
                  </div>
                </div>

                <div className="mt-3 text-center">
                  <span className="text-xs font-semibold text-slate-500 bg-slate-100/80 px-4 py-1.5 rounded-full border border-slate-200">
                    {currentTime.toLocaleDateString('en-IN', {
                    weekday: 'long'
                  })}
                  </span>
                </div>

                <div className="mt-3 flex items-center justify-center gap-4 text-[10px] font-medium text-slate-400">
                  <span>Session: 2026-27</span>
                  <span className="w-px h-3 bg-slate-300"></span>
                </div>
              </div>
            </div>

          </div>

        </div>

        <Footer />
      </div>
    </div>;
}

// ─── ADMISSIONS ───────────────────────────────────────────────────────────────

function AdmissionStudentList({
  cls,
  section,
  onBack,
  group
}) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  let allStudents = getStudents(cls).filter(s => s.section === section);
  if (group) allStudents = allStudents.filter(s => s.group === group);
  const filtered = allStudents.filter(s => {
    const ms = s.name.toLowerCase().includes(search.toLowerCase());
    const mf = statusFilter === "all" || s.admissionStatus === statusFilter;
    return ms && mf;
  });
  const counts = {
    joined: allStudents.filter(s => s.admissionStatus === "joined").length,
    pending: allStudents.filter(s => s.admissionStatus === "pending").length,
    approval: allStudents.filter(s => s.admissionStatus === "approval").length,
    inprogress: allStudents.filter(s => s.admissionStatus === "inprogress").length
  };
  const backLabel = group ? `${cls} — ${group}` : `${cls} — Sections`;
  return <div className="animate-fade-in-up">
      <BackBtn onClick={onBack} label={backLabel} />
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800">{cls} — Section {section} · Admissions</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">{allStudents.length} students · 2026–27</p>
        </div>
        <button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-bold px-6 py-3 rounded-xl shadow-lg shadow-blue-500/25 transition-all duration-200 hover:-translate-y-0.5 mobile-tap-target">
          + New Admission
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard icon="" value="2,847" label="Total Students" trend="+124" trendUp gradient="bg-gradient-to-br from-blue-600 to-blue-800" />
        <StatCard icon="" value="94.2%" label="Today's Attendance" trend="+1.3%" trendUp gradient="bg-gradient-to-br from-blue-500 to-blue-700" />
        <StatCard icon="" value="₹18.4L" label="Fees Collected — April" trend="82%" trendUp gradient="bg-gradient-to-br from-blue-500 to-blue-600" />
        <StatCard icon="" value="47" label="New Enquiries" trend="-3" trendUp={false} gradient="bg-gradient-to-br from-blue-500 to-blue-700" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[{
        key: "joined",
        label: "Joined",
        color: "text-blue-600",
        bg: "bg-blue-50",
        border: "border-blue-200",
        icon: <CheckCircle2 className="inline w-[1em] h-[1em] align-[-0.125em]" />
      }, {
        key: "pending",
        label: "Pending",
        color: "text-blue-600",
        bg: "bg-blue-50",
        border: "border-blue-200",
        icon: "⏳"
      }, {
        key: "approval",
        label: "Approval",
        color: "text-blue-600",
        bg: "bg-blue-50",
        border: "border-blue-200",
        icon: <FileEdit className="inline w-[1em] h-[1em] align-[-0.125em]" />
      }, {
        key: "inprogress",
        label: "In Progress",
        color: "text-blue-600",
        bg: "bg-blue-50",
        border: "border-blue-200",
        icon: <RefreshCw className="inline w-[1em] h-[1em] align-[-0.125em]" />
      }].map(st => <div key={st.key} onClick={() => setStatusFilter(statusFilter === st.key ? "all" : st.key)} className={`${st.bg} border ${st.border} rounded-2xl p-5 text-center cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md ${statusFilter === st.key ? "ring-2 ring-offset-2 ring-blue-400 shadow-lg scale-105" : "hover:scale-102"}`}>
            <div className="text-2xl mb-2">{st.icon}</div>
            <div className={`text-3xl font-extrabold ${st.color}`}><AnimatedCounter value={counts[st.key]} /></div>
            <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-2 font-bold">{st.label}</div>
          </div>)}
      </div>

      <GlassCard>
        <div className="flex gap-4 p-5 border-b border-slate-100 flex-wrap items-center">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Search className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
            <input value={search} onChange={e => setSearch(e.target.value)} className="bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-700 outline-none w-56 placeholder-slate-400 shadow-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-300 transition-all" placeholder="Search student..." />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 outline-none cursor-pointer shadow-sm focus:ring-2 focus:ring-blue-100">
            <option value="all">All Status</option>
            <option value="joined">Joined</option>
            <option value="pending">Pending</option>
            <option value="approval">Approval</option>
            <option value="inprogress">In Progress</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white">
                {["#", "Roll No", "Student Name", "Section", "Attendance", "Term Fee", "Paid", "Balance", "Status", "Action"].map(h => <th key={h} className="text-[11px] text-slate-500 font-bold uppercase tracking-wide px-5 py-4 text-left">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => <tr key={i} className="border-b border-slate-50 hover:bg-blue-50/30 transition-colors duration-200">
                  <td className="px-5 py-4 text-sm text-slate-400 font-medium">{i + 1}</td>
                  <td className="px-5 py-4 text-sm text-slate-500 font-mono">{s.roll}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold shadow-md ${s.avBg} ${s.avColor}`}>
                        {s.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                      </div>
                      <span className="text-sm font-bold text-slate-800">{s.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-600 font-medium">{s.section}</td>
                  <td className="px-5 py-4 text-sm text-slate-600">{s.attendance}</td>
                  <td className="px-5 py-4 text-base text-slate-800 font-bold">₹{(s.feeTotal || 0).toLocaleString()}</td>
                  <td className="px-5 py-4 text-base text-blue-700 font-bold">₹{(s.feePaid || 0).toLocaleString()}</td>
                  <td className={`px-5 py-4 text-base font-bold ${s.feeTotal - (s.feePaid || 0) > 0 ? "text-blue-600" : "text-blue-700"}`}>
                    ₹{((s.feeTotal || 0) - (s.feePaid || 0)).toLocaleString()}
                  </td>
                  <td className="px-5 py-4"><AdmStatusBadge status={s.admissionStatus} /></td>
                  <td className="px-5 py-4 text-sm text-blue-500 font-semibold cursor-pointer hover:text-blue-700 transition-colors">View <ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>;
}

// ─── ADMISSION SECTIONS ──────────────────────────────────────────────────────

function AdmissionSections({
  cls,
  onBack
}) {
  const [selGroup, setSelGroup] = useState(null);
  const [selSection, setSelSection] = useState(null);
  const students = getStudents(cls);
  const sections = getSectionsForClass(cls);
  const isSenior = cls === "Class 11" || cls === "Class 12";
  if (isSenior && selGroup && selSection) {
    return <AdmissionStudentList cls={cls} section={selSection} onBack={() => setSelSection(null)} group={selGroup} />;
  }
  if (isSenior && selGroup) {
    const groupStudents = students.filter(s => s.group === selGroup);
    const secList = getSectionsForClass(cls);
    return <div className="animate-fade-in-up">
        <BackBtn onClick={() => setSelGroup(null)} label={`${cls} — Groups`} />
        <div className="mb-6">
          <h2 className="text-2xl font-extrabold text-slate-800">{cls} — {selGroup} — Sections</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">{groupStudents.length} students in this group</p>
        </div>
        <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">Select Section</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {secList.map((sec, i) => {
          const col = CLASS_COLORS[i % CLASS_COLORS.length];
          const secStudents = groupStudents.filter(s => s.section === sec);
          const joined = secStudents.filter(s => s.admissionStatus === "joined").length;
          const pending = secStudents.filter(s => s.admissionStatus === "pending").length;
          return <div key={sec} onClick={() => setSelSection(sec)} className={`bg-white border ${col.border} rounded-2xl p-6 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 shadow-sm`}>
                <div className="text-sm font-bold text-slate-700 mb-3">Section {sec}</div>
                <div className={`text-4xl font-extrabold ${col.text} mb-3`}><AnimatedCounter value={secStudents.length} /></div>
                <div className="flex gap-4 text-[11px] font-bold">
                  <span className="text-blue-600 flex items-center gap-1"><Check className="inline w-[1em] h-[1em] align-[-0.125em]" /> {joined}</span>
                  <span className="text-blue-600 flex items-center gap-1">⏳ {pending}</span>
                </div>
              </div>;
        })}
        </div>
      </div>;
  }
  if (isSenior) {
    const groups = [...new Set(students.map(s => s.group))].filter(g => g).sort();
    if (groups.length === 0) {
      return <div className="animate-fade-in-up">
          <BackBtn onClick={onBack} label="All Classes" />
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
            <div>
              <h2 className="text-2xl font-extrabold text-slate-800">{cls} — Groups</h2>
              <p className="text-sm text-slate-400 mt-1 font-medium">{students.length} students total</p>
            </div>
            <button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-bold px-6 py-3 rounded-xl shadow-lg shadow-blue-500/25 transition-all duration-200 hover:-translate-y-0.5 mobile-tap-target">
              + New Admission
            </button>
          </div>
          <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">Subject Groups</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
            {SENIOR_GROUPS.map((g, i) => {
            const grpStudents = students.filter(s => s.group === g.label);
            const joined = grpStudents.filter(s => s.admissionStatus === "joined").length;
            const pending = grpStudents.filter(s => s.admissionStatus === "pending").length;
            return <div key={i} onClick={() => setSelGroup(g.label)} className={`border ${g.border} ${g.bg} rounded-2xl p-6 cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}>
                  <div className="text-3xl mb-3">{g.icon}</div>
                  <div className={`text-sm font-bold ${g.text} mb-2`}>{g.label}</div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {g.subjects.map(sub => <span key={sub} className={`text-[10px] font-semibold px-2 py-1 rounded-md bg-white/70 ${g.text} border border-white/50 shadow-sm`}>{sub}</span>)}
                  </div>
                  <div className="flex gap-3 text-[11px] font-bold">
                    <span className="text-blue-600"><Check className="inline w-[1em] h-[1em] align-[-0.125em]" /> {joined} Joined</span>
                    <span className="text-blue-600">⏳ {pending} Pending</span>
                  </div>
                  <div className={`mt-3 text-[11px] font-bold ${g.text} flex items-center gap-1`}>
                    View Admissions <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                  </div>
                </div>;
          })}
          </div>
        </div>;
    }
    return <div className="animate-fade-in-up">
        <BackBtn onClick={onBack} label="All Classes" />
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-extrabold text-slate-800">{cls} — Groups</h2>
            <p className="text-sm text-slate-400 mt-1 font-medium">{students.length} students total</p>
          </div>
          <button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-bold px-6 py-3 rounded-xl shadow-lg shadow-blue-500/25 transition-all duration-200 hover:-translate-y-0.5 mobile-tap-target">
            + New Admission
          </button>
        </div>
        <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">Subject Groups</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {groups.map((grp, i) => {
          const col = CLASS_COLORS[i % CLASS_COLORS.length];
          const grpStudents = students.filter(s => s.group === grp);
          const joined = grpStudents.filter(s => s.admissionStatus === "joined").length;
          const pending = grpStudents.filter(s => s.admissionStatus === "pending").length;
          return <div key={grp} onClick={() => setSelGroup(grp)} className={`bg-gradient-to-br ${col.bg} border ${col.border} rounded-2xl p-6 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 shadow-sm`}>
                <div className="text-sm font-bold text-slate-700 mb-3">{grp}</div>
                <div className={`text-4xl font-extrabold ${col.text} mb-3`}><AnimatedCounter value={grpStudents.length} /></div>
                <div className="flex gap-4 text-[11px] font-bold">
                  <span className="text-blue-600"><Check className="inline w-[1em] h-[1em] align-[-0.125em]" /> {joined}</span>
                  <span className="text-blue-600">⏳ {pending}</span>
                </div>
              </div>;
        })}
        </div>
      </div>;
  }
  if (selSection) return <AdmissionStudentList cls={cls} section={selSection} onBack={() => setSelSection(null)} />;
  return <div className="animate-fade-in-up">
      <BackBtn onClick={onBack} label="All Classes" />
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800">{cls} — Admission Sections</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">{students.length} students total · Click a section to view admissions</p>
        </div>
        <button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-bold px-6 py-3 rounded-xl shadow-lg shadow-blue-500/25 transition-all duration-200 hover:-translate-y-0.5 mobile-tap-target">
          + New Admission
        </button>
      </div>
      <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">All Sections</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {sections.map((sec, i) => {
        const col = CLASS_COLORS[i % CLASS_COLORS.length];
        const secStudents = students.filter(s => s.section === sec);
        const joined = secStudents.filter(s => s.admissionStatus === "joined").length;
        const pending = secStudents.filter(s => s.admissionStatus === "pending").length;
        const approval = secStudents.filter(s => s.admissionStatus === "approval").length;
        const inprogress = secStudents.filter(s => s.admissionStatus === "inprogress").length;
        return <div key={sec} onClick={() => setSelSection(sec)} className={`bg-gradient-to-br ${col.bg} border ${col.border} rounded-2xl p-6 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 shadow-sm group`}>
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-lg font-bold shadow-md ${col.bg} ${col.text} group-hover:scale-110 transition-transform duration-300`}>
                  {sec.split(" ")[0]}
                </div>
                <div>
                  <div className="text-base font-bold text-slate-700">Section {sec}</div>
                  <div className="text-[11px] text-slate-400 mt-1 font-medium">{secStudents.length} students</div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-[11px]">
                  <span className="text-slate-400 font-medium">Joined</span>
                  <span className="font-bold text-blue-600">{joined}</span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-slate-400 font-medium">Pending</span>
                  <span className="font-bold text-blue-600">{pending}</span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-slate-400 font-medium">Approval</span>
                  <span className="font-bold text-blue-600">{approval}</span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-slate-400 font-medium">In Progress</span>
                  <span className="font-bold text-blue-600">{inprogress}</span>
                </div>
              </div>
              <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-blue-400 to-blue-500 rounded-full transition-all duration-700" style={{
              width: Math.round(joined / secStudents.length * 100) + "%"
            }}></div>
              </div>
              <div className={`mt-4 text-[11px] font-bold ${col.text} flex items-center gap-1 group-hover:gap-2 transition-all`}>
                View Students <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
              </div>
            </div>;
      })}
      </div>
    </div>;
}

// ─── ADMISSIONS MAIN ─────────────────────────────────────────────────────────

function Admissions() {
  const [selClass, setSelClass] = useState(null);
  if (selClass) return <AdmissionSections cls={selClass} onBack={() => setSelClass(null)} />;
  const allStudents = allClasses.flatMap(c => getStudents(c));
  const totalJoined = allStudents.filter(s => s.admissionStatus === "joined").length;
  const totalPending = allStudents.filter(s => s.admissionStatus === "pending").length;
  const totalApproval = allStudents.filter(s => s.admissionStatus === "approval").length;
  const totalInprogress = allStudents.filter(s => s.admissionStatus === "inprogress").length;
  return <div className="animate-fade-in-up">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800">Admissions 2026–27</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">Click a class to view section-wise admissions</p>
        </div>
        <button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-sm font-bold px-6 py-3 rounded-xl shadow-lg shadow-blue-500/25 transition-all duration-200 hover:-translate-y-0.5 mobile-tap-target">
          + New Admission
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[{
        v: totalJoined,
        l: "Joined",
        c: "text-blue-600",
        bg: "bg-blue-50",
        border: "border-blue-200",
        icon: <CheckCircle2 className="inline w-[1em] h-[1em] align-[-0.125em]" />
      }, {
        v: totalPending,
        l: "Pending",
        c: "text-blue-600",
        bg: "bg-blue-50",
        border: "border-blue-200",
        icon: "⏳"
      }, {
        v: totalApproval,
        l: "Approval",
        c: "text-blue-600",
        bg: "bg-blue-50",
        border: "border-blue-200",
        icon: <FileEdit className="inline w-[1em] h-[1em] align-[-0.125em]" />
      }, {
        v: totalInprogress,
        l: "In Progress",
        c: "text-blue-600",
        bg: "bg-blue-50",
        border: "border-blue-200",
        icon: <RefreshCw className="inline w-[1em] h-[1em] align-[-0.125em]" />
      }].map(({
        v,
        l,
        c,
        bg,
        border,
        icon
      }, i) => <GlassCard key={i}>
            <div className={`${bg} border ${border} rounded-2xl p-5 text-center`}>
              <div className="text-2xl mb-2">{icon}</div>
              <div className={`text-3xl font-extrabold ${c}`}><AnimatedCounter value={v} /></div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wide mt-2 font-bold">{l}</div>
            </div>
          </GlassCard>)}
      </div>

      <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">Select Class to View Admissions</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
        {allClasses.map((cls, i) => {
        const col = CLASS_COLORS[i % CLASS_COLORS.length];
        const stus = getStudents(cls);
        const joined = stus.filter(s => s.admissionStatus === "joined").length;
        const pending = stus.filter(s => s.admissionStatus === "pending").length;
        const label = cls === "UKG" ? "UKG" : cls === "LKG" ? "LKG" : cls.replace("Class ", "C");
        return <div key={cls} onClick={() => setSelClass(cls)} className={`bg-gradient-to-br ${col.bg} border ${col.border} rounded-2xl p-5 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 shadow-sm group`}>
              <div className={`w-14 h-14 rounded-2xl mb-4 flex items-center justify-center text-lg font-bold shadow-md ${col.bg} ${col.text} group-hover:scale-110 transition-transform duration-300`}>
                {label}
              </div>
              <div className="text-base font-bold text-slate-700">{cls}</div>
              <div className="mt-3 space-y-2">
                <div className="flex justify-between text-[11px]">
                  <span className="text-slate-400 font-medium">Total</span>
                  <span className="font-semibold text-slate-600">{stus.length}</span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-slate-400 font-medium">Joined</span>
                  <span className="font-bold text-blue-600">{joined}</span>
                </div>
                <div className="flex justify-between text-[11px]">
                  <span className="text-slate-400 font-medium">Pending</span>
                  <span className="font-bold text-blue-600">{pending}</span>
                </div>
              </div>
              <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-blue-400 to-blue-500 transition-all duration-700" style={{
              width: Math.round(joined / stus.length * 100) + "%"
            }}></div>
              </div>
              <div className={`mt-4 text-[11px] font-bold ${col.text} flex items-center gap-1 group-hover:gap-2 transition-all`}>
                View Sections <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
              </div>
            </div>;
      })}
      </div>
    </div>;
}

// ─── STUDENTS ─────────────────────────────────────────────────────────────────

function StudentList({
  cls,
  section,
  onBack,
  group,
  onViewStudent
}) {
  const [search, setSearch] = useState("");
  let allStudents = getStudents(cls).filter(s => s.section === section);
  if (group) allStudents = allStudents.filter(s => s.group === group);
  const filtered = allStudents.filter(s => s.name.toLowerCase().includes(search.toLowerCase()));
  const backLabel = group ? `${cls} — ${group}` : `${cls} — Sections`;
  return <div className="animate-fade-in-up">
      <BackBtn onClick={onBack} label={backLabel} />
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800">{cls} — Section {section} · Students</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">{allStudents.length} students · 2026–27</p>
        </div>
      </div>

      <GlassCard>
        <div className="flex gap-4 p-5 border-b border-slate-100">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Search className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
            <input value={search} onChange={e => setSearch(e.target.value)} className="bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-700 outline-none w-56 placeholder-slate-400 shadow-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-300 transition-all" placeholder="Search student..." />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white">
                {["#", "Roll No", "Student Name", "Section", "Attendance", "Term Fee", "Paid", "Balance", "Status", "Action"].map(h => <th key={h} className="text-[11px] text-slate-500 font-bold uppercase tracking-wide px-5 py-4 text-left">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.map((s, i) => <tr key={i} className="border-b border-slate-50 hover:bg-blue-50/30 transition-colors duration-200">
                  <td className="px-5 py-4 text-sm text-slate-400 font-medium">{i + 1}</td>
                  <td className="px-5 py-4 text-sm text-slate-500 font-mono">{s.roll}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold shadow-md ${s.avBg} ${s.avColor}`}>
                        {s.name.split(" ").map(n => n[0]).join("").slice(0, 2)}
                      </div>
                      <div>
                        <div className="text-sm font-bold text-slate-800">{s.name}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">Parent: {s.parent}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm text-slate-600 font-medium">{s.section}</td>
                  <td className="px-5 py-4 text-sm text-slate-600">{s.attendance}</td>
                  <td className="px-5 py-4 text-base text-slate-800 font-bold">₹{(s.feeTotal || 0).toLocaleString()}</td>
                  <td className="px-5 py-4 text-base text-blue-700 font-bold">₹{(s.feePaid || 0).toLocaleString()}</td>
                  <td className={`px-5 py-4 text-base font-bold ${s.feeTotal - (s.feePaid || 0) > 0 ? "text-blue-600" : "text-blue-700"}`}>
                    ₹{((s.feeTotal || 0) - (s.feePaid || 0)).toLocaleString()}
                  </td>
                  <td className="px-5 py-4"><AdmStatusBadge status={s.admissionStatus} /></td>
                  <td onClick={() => onViewStudent(s)} className="px-5 py-4 text-sm text-blue-500 font-semibold cursor-pointer hover:text-blue-700 transition-colors">View <ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>;
}

// ─── STUDENT SECTIONS ────────────────────────────────────────────────────────

function StudentSections({
  cls,
  onBack,
  onViewStudent
}) {
  const [selGroup, setSelGroup] = useState(null);
  const [selSection, setSelSection] = useState(null);
  const students = getStudents(cls);
  const sections = getSectionsForClass(cls);
  const isSenior = cls === "Class 11" || cls === "Class 12";
  if (isSenior && selGroup && selSection) {
    return <StudentList cls={cls} section={selSection} onBack={() => setSelSection(null)} group={selGroup} onViewStudent={onViewStudent} />;
  }
  if (isSenior && selGroup) {
    const groupStudents = students.filter(s => s.group === selGroup);
    const secList = getSectionsForClass(cls);
    return <div className="animate-fade-in-up">
        <BackBtn onClick={() => setSelGroup(null)} label={`${cls} — Groups`} />
        <div className="mb-6">
          <h2 className="text-2xl font-extrabold text-slate-800">{cls} — {selGroup} — Sections</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">{groupStudents.length} students in this group</p>
        </div>
        <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">Select Section</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {secList.map((sec, i) => {
          const col = CLASS_COLORS[i % CLASS_COLORS.length];
          const secStudents = groupStudents.filter(s => s.section === sec);
          return <div key={sec} onClick={() => setSelSection(sec)} className={`bg-gradient-to-br ${col.bg} border ${col.border} rounded-2xl p-6 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 shadow-sm`}>
                <div className="text-sm font-bold text-slate-700 mb-3">Section {sec}</div>
                <div className={`text-4xl font-extrabold ${col.text} mb-3`}><AnimatedCounter value={secStudents.length} /></div>
                <div className="text-[11px] text-slate-400 font-medium">Students</div>
                <div className={`mt-3 text-[11px] font-bold ${col.text} flex items-center gap-1`}>
                  View Students <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                </div>
              </div>;
        })}
        </div>
      </div>;
  }
  if (isSenior) {
    const groups = [...new Set(students.map(s => s.group))].filter(g => g).sort();
    if (groups.length === 0) {
      return <div className="animate-fade-in-up">
          <BackBtn onClick={onBack} label="All Classes" />
          <div className="mb-6">
            <h2 className="text-2xl font-extrabold text-slate-800">{cls} — Groups</h2>
            <p className="text-sm text-slate-400 mt-1 font-medium">{students.length} students total</p>
          </div>
          <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">Subject Groups</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
            {SENIOR_GROUPS.map((g, i) => {
            const grpStudents = students.filter(s => s.group === g.label);
            return <div key={i} onClick={() => setSelGroup(g.label)} className={`border ${g.border} ${g.bg} rounded-2xl p-6 cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}>
                  <div className="text-3xl mb-3">{g.icon}</div>
                  <div className={`text-sm font-bold ${g.text} mb-2`}>{g.label}</div>
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {g.subjects.map(sub => <span key={sub} className={`text-[10px] font-semibold px-2 py-1 rounded-md bg-white/70 ${g.text} border border-white/50 shadow-sm`}>{sub}</span>)}
                  </div>
                  <div className="text-sm font-bold text-slate-700">{grpStudents.length} Students</div>
                  <div className={`mt-3 text-[11px] font-bold ${g.text} flex items-center gap-1`}>
                    View Sections <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                  </div>
                </div>;
          })}
          </div>
        </div>;
    }
    return <div className="animate-fade-in-up">
        <BackBtn onClick={onBack} label="All Classes" />
        <div className="mb-6">
          <h2 className="text-2xl font-extrabold text-slate-800">{cls} — Groups</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">{students.length} students total</p>
        </div>
        <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">Subject Groups</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {groups.map((grp, i) => {
          const col = CLASS_COLORS[i % CLASS_COLORS.length];
          const grpStudents = students.filter(s => s.group === grp);
          return <div key={grp} onClick={() => setSelGroup(grp)} className={`bg-gradient-to-br ${col.bg} border ${col.border} rounded-2xl p-6 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 shadow-sm`}>
                <div className="text-sm font-bold text-slate-700 mb-3">{grp}</div>
                <div className={`text-4xl font-extrabold ${col.text} mb-3`}><AnimatedCounter value={grpStudents.length} /></div>
                <div className="text-[11px] text-slate-400 font-medium">Students</div>
                <div className={`mt-3 text-[11px] font-bold ${col.text} flex items-center gap-1`}>
                  View Sections <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                </div>
              </div>;
        })}
        </div>
      </div>;
  }
  if (selSection) return <StudentList cls={cls} section={selSection} onBack={() => setSelSection(null)} onViewStudent={onViewStudent} />;
  return <div className="animate-fade-in-up">
      <BackBtn onClick={onBack} label="All Classes" />
      <div className="mb-6">
        <h2 className="text-2xl font-extrabold text-slate-800">{cls} — Sections</h2>
        <p className="text-sm text-slate-400 mt-1 font-medium">{students.length} students total · Click a section to view students</p>
      </div>
      <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">All Sections</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {sections.map((sec, i) => {
        const col = CLASS_COLORS[i % CLASS_COLORS.length];
        const secStudents = students.filter(s => s.section === sec);
        return <div key={sec} onClick={() => setSelSection(sec)} className={`bg-gradient-to-br ${col.bg} border ${col.border} rounded-2xl p-6 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 shadow-sm group`}>
              <div className="flex items-center gap-4 mb-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-lg font-bold shadow-md ${col.bg} ${col.text} group-hover:scale-110 transition-transform duration-300`}>
                  {sec.split(" ")[0]}
                </div>
                <div>
                  <div className="text-base font-bold text-slate-700">Section {sec}</div>
                  <div className="text-[11px] text-slate-400 mt-1 font-medium">{secStudents.length} students</div>
                </div>
              </div>
              <div className={`mt-4 text-[11px] font-bold ${col.text} flex items-center gap-1 group-hover:gap-2 transition-all`}>
                View Students <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
              </div>
            </div>;
      })}
      </div>
    </div>;
}

// ─── STUDENTS MAIN ───────────────────────────────────────────────────────────

function Students() {
  const [selClass, setSelClass] = useState(null);
  const [selStudent, setSelStudent] = useState(null);
  if (selStudent) {
    return <StudentView student={selStudent} onBack={() => setSelStudent(null)} />;
  }
  if (selClass) return <StudentSections cls={selClass} onBack={() => setSelClass(null)} onViewStudent={setSelStudent} />;
  const total = allClasses.reduce((s, c) => s + getStudents(c).length, 0);
  return <div className="animate-fade-in-up">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800">Students Directory</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">UKG to Class 12 — {total.toLocaleString()} students enrolled</p>
        </div>
      </div>
      <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">Select Class to View Students</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
        {(() => {
        const ordered = [];
        const prefer = ["LKG", "UKG", "Class 1", "Class 2"];
        prefer.forEach(p => {
          if (allClasses.includes(p)) ordered.push(p);
        });
        allClasses.forEach(c => {
          if (!ordered.includes(c)) ordered.push(c);
        });
        return ordered.map((cls, i) => {
          const col = CLASS_COLORS[i % CLASS_COLORS.length];
          const count = getStudents(cls).length;
          const label = cls === "UKG" ? "UKG" : cls === "LKG" ? "LKG" : cls.replace("Class ", "C");
          return <div key={cls} onClick={() => setSelClass(cls)} className={`bg-gradient-to-br ${col.bg} border ${col.border} rounded-2xl p-5 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 shadow-sm group`}>
                <div className={`w-14 h-14 rounded-2xl mb-4 flex items-center justify-center text-lg font-bold shadow-md ${col.bg} ${col.text} group-hover:scale-110 transition-transform duration-300`}>
                  {label}
                </div>
                <div className="text-base font-bold text-slate-700">{cls}</div>
                <div className="mt-3 space-y-2">
                  <div className="flex justify-between text-[11px]">
                    <span className="text-slate-400 font-medium">Total Students</span>
                    <span className="font-semibold text-slate-600">{count}</span>
                  </div>
                </div>
                <div className={`mt-4 text-[11px] font-bold ${col.text} flex items-center gap-1 group-hover:gap-2 transition-all`}>
                  View Sections <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                </div>
              </div>;
        });
      })()}
      </div>
    </div>;
}

// ─── STAFF ────────────────────────────────────────────────────────────────────

function StaffCard({
  s,
  onClick
}) {
  const theme = getStaffDeptTheme(s.dept);
  const initials = s.initials || (s.name || "").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return <div onClick={onClick} style={{
    background: theme.bg,
    borderColor: theme.border
  }} className="border rounded-xl p-5 shadow-sm cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group">
      <div className="flex items-center gap-3 mb-4">
        <div style={{
        background: "rgba(255,255,255,0.2)",
        borderColor: "rgba(255,255,255,0.35)",
        color: "#ffffff"
      }} className="w-12 h-12 rounded-xl flex items-center justify-center text-sm font-bold shadow-md transition-transform duration-300 group-hover:scale-110 backdrop-blur-sm border">
          {initials}
        </div>
        <div>
          <div className="text-[14px] font-bold" style={{
          color: theme.text
        }}>{s.name}</div>
          <div className="text-[11px] mt-0.5" style={{
          color: theme.subtext
        }}>{s.role} · {s.dept}</div>
        </div>
      </div>
      <div className="space-y-2 text-[12px]">
        <div className="flex justify-between">
          <span style={{
          color: theme.subtext
        }}>Experience</span>
          <span className="font-medium" style={{
          color: theme.text
        }}>{s.exp} {typeof s.exp === 'number' ? 'yrs' : ''}</span>
        </div>
        <div className="flex justify-between">
          <span style={{
          color: theme.subtext
        }}>Phone</span>
          <span className="font-medium" style={{
          color: theme.text
        }}>{s.phone || "—"}</span>
        </div>
      </div>

    </div>;
}
function StaffDetail({
  s,
  onBack
}) {
  const avBg = s.avBg || "bg-slate-100";
  const avColor = s.avColor || "text-slate-700";
  const initials = s.initials || (s.name || "").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  return <div className="animate-fade-in-up">
      <BackBtn onClick={onBack} label="Staff Management" />
      <div className="w-full">
        <GlassCard>
          <div className="p-7">
            <div className="flex items-center gap-5 mb-6 pb-6 border-b border-slate-100">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center text-xl font-bold shadow-lg ${avBg} ${avColor}`}>
                {initials}
              </div>
              <div>
                <div className="text-xl font-bold text-slate-800">{s.name}</div>
                <div className="text-sm text-slate-500 mt-1">{s.role} · {s.dept}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[["Department", s.dept], ["Experience", s.exp ? `${s.exp} ${typeof s.exp === 'number' ? 'yrs' : ''}` : "—"], ["Qualification", s.qual || "—"], ["Phone", s.phone || "—"], ["Email", s.email || "—"], ["Joined", s.join || "—"]].map(([k, v]) => <div key={k} className="bg-gradient-to-br from-blue-50/40 to-white rounded-xl p-4 border border-slate-100">
                  <div className="text-[11px] text-slate-400 uppercase tracking-wide mb-1 font-bold">{k}</div>
                  <div className="text-sm font-semibold text-slate-700">{v}</div>
                </div>)}
            </div>
          </div>
        </GlassCard>
      </div>
    </div>;
}
function Staff() {
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchErr, setFetchErr] = useState(false);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selGroup, setSelGroup] = useState(null);
  const [selStaff, setSelStaff] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("https://schoolcrm-fzcv.onrender.com/api/staff");
        if (!res.ok) throw new Error("fetch failed");
        const data = await res.json();
        setStaffList(data);
      } catch (err) {
        console.error("Staff fetch error:", err);
        setFetchErr(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  if (selStaff) return <StaffDetail s={selStaff} onBack={() => setSelStaff(null)} />;
  if (loading) return <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-slate-100 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
        <div className="text-slate-500 font-bold text-lg animate-pulse">Loading Staff...</div>
      </div>
    </div>;
  if (fetchErr) return <div className="flex items-center justify-center h-64">
      <div className="text-center">
        <div className="text-5xl mb-4 animate-float"><AlertTriangle className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
        <div className="text-red-500 font-semibold text-sm">Could not load staff. Is the backend running on port 5000?</div>
      </div>
    </div>;
  const totalStaff = staffList.length;
  const totalPresent = staffList.filter(s => s.status === "present").length;
  if (selGroup) {
    const groupStaff = staffList.filter(s => s.dept === selGroup.dept);
    const groupTheme = getStaffDeptTheme(selGroup.dept);
    return <div className="animate-fade-in-up">
        <BackBtn onClick={() => setSelGroup(null)} label="Staff Management" />
        <div className="mb-6">
          <h2 className="text-2xl font-extrabold text-slate-800">{selGroup.label} — Staff</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">
            Department: {selGroup.label} · {groupStaff.length} staff registered
          </p>
        </div>
        <div className="mb-6 p-5 rounded-2xl border flex gap-3 flex-wrap shadow-sm" style={{
        background: groupTheme.bg,
        borderColor: groupTheme.border
      }}>
          {selGroup.subjects.map(sub => <span key={sub} style={{
          borderColor: groupTheme.border,
          color: groupTheme.chipText
        }} className="text-xs font-semibold px-4 py-1.5 rounded-full bg-white border shadow-sm">
              {selGroup.icon} {sub}
            </span>)}
        </div>
        {groupStaff.length === 0 ? <div className="text-center py-16">
            <div className="text-5xl mb-4 animate-float"><User className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
            <div className="text-slate-400 font-semibold text-sm">No staff registered under <strong>{selGroup.label}</strong> yet.</div>
          </div> : <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {groupStaff.map((s, i) => <StaffCard key={s._id || i} s={s} onClick={() => setSelStaff(s)} />)}
          </div>}
      </div>;
  }
  const filteredBase = filter === "all" ? staffList : staffList.filter(s => s.type === filter);
  const filtered = filteredBase.filter(s => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return true;
    return (s.name || "").toLowerCase().includes(q) || (s.dept || "").toLowerCase().includes(q) || (s.role || "").toLowerCase().includes(q);
  });
  const searchResults = (search || "").trim() ? filteredBase.filter(s => {
    const q = search.trim().toLowerCase();
    return (s.name || "").toLowerCase().includes(q) || (s.dept || "").toLowerCase().includes(q) || (s.role || "").toLowerCase().includes(q) || (s.phone || "").toString().toLowerCase().includes(q) || (s.qual || "").toLowerCase().includes(q);
  }).slice(0, 6) : [];
  return <div className="animate-fade-in-up">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800">Staff Management</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">
            {totalPresent} of {totalStaff} staff present today
          </p>
        </div>
      </div>

      <div className="sticky top-[72px] z-40 bg-white/85 backdrop-blur-md md:bg-transparent md:static p-3 md:p-0 mb-4 border-b md:border-0">
        <div className="max-w-md md:max-w-none mx-auto md:mx-0">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"><Search className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
            <input value={search} onChange={e => setSearch(e.target.value)} className="bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm w-full md:w-64 placeholder-slate-400 shadow-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-300 transition-all" placeholder="Search staff name, dept..." />

            {searchResults.length > 0 && <div className="absolute left-0 right-0 mt-2 bg-white border border-slate-200 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto">
                {searchResults.map((r, idx) => <button key={r._id || idx} onClick={() => {
              setSelStaff(r);
              setSearch('');
            }} className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold ${r.avBg || 'bg-slate-100'} ${r.avColor || 'text-slate-700'}`}>
                      {(r.initials || (r.name || '').slice(0, 2)).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-slate-800 truncate">{r.name}</div>
                      <div className="text-xs text-slate-400 truncate">{r.role || '—'} · {r.dept || '—'}</div>
                    </div>
                  </button>)}
              </div>}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2.5 mb-6">
        {["all", "Teaching", "Non-Teaching", "Admin"].map(t => <button key={t} onClick={() => setFilter(t)} className={`px-4 py-2.5 rounded-xl text-xs font-bold border transition-all duration-200 shadow-sm text-center ${filter === t ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white border-blue-600 shadow-blue-200" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
            {t === "all" ? "All Staff" : t}
          </button>)}
      </div>

      {(filter === "all" || filter === "Teaching") && <div className="mb-6">
          <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">
            Class 11 &amp; 12 — Subject Groups
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {SENIOR_GROUPS.map((g, i) => {
          const deptCount = staffList.filter(s => s.dept === g.dept).length;
          const t = getStaffDeptTheme(g.dept);
          return <div key={i} onClick={() => setSelGroup(g)} style={{
            background: t.bg,
            borderColor: t.border
          }} className="border rounded-2xl p-5 cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 shadow-sm">
                  <div className="text-3xl mb-3" style={{
              color: t.text
            }}>{g.icon}</div>
                  <div className="text-sm font-bold mb-1" style={{
              color: t.text
            }}>{g.label}</div>
                  <div className="text-[11px] mb-1 font-medium" style={{
              color: t.subtext
            }}>{g.subjects.length} subjects</div>
                  <div className="text-[11px] font-semibold mb-1" style={{
              color: t.text
            }}>{deptCount} staff registered</div>
                  <div className="text-[11px] font-bold flex items-center gap-1" style={{
              color: t.text
            }}>
                    View Staff <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                  </div>
                </div>;
        })}
          </div>
        </div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {filtered.map((s, i) => <StaffCard key={s._id || i} s={s} onClick={() => setSelStaff(s)} />)}
        {filtered.length === 0 && <div className="col-span-1 text-center py-16">
            <div className="text-5xl mb-4 animate-float"><User className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
            <div className="text-slate-400 font-semibold text-sm">No staff found for this filter.</div>
          </div>}
      </div>
    </div>;
}

// ─── ATTENDANCE ───────────────────────────────────────────────────────────────

// className key exactly matching how the Class Advisor's attendance-marking
// page (StudentProfile.js) saves records: "Class 1-A" for regular classes,
// or "Class 11 Bio Maths-A" for senior groups. Same convention as
// buildTimetableKey used by the Timetable view below.
function buildAttendanceKey(cls, section, group) {
  return group ? `${cls} ${group}-${section}` : `${cls}-${section}`;
}
function todayISTStr() {
  const d = new Date(Date.now() + 5.5 * 3600000);
  return d.toISOString().slice(0, 10);
}
function AttendanceStatusBadge({
  status
}) {
  const cfg = {
    P: {
      label: "Present",
      bg: "bg-blue-50",
      text: "text-blue-600",
      border: "border-blue-200"
    },
    A: {
      label: "Absent",
      bg: "bg-red-50",
      text: "text-red-600",
      border: "border-red-200"
    },
    L: {
      label: "Late",
      bg: "bg-amber-50",
      text: "text-amber-600",
      border: "border-amber-200"
    }
  }[status] || {
    label: "Not Marked",
    bg: "bg-slate-50",
    text: "text-slate-400",
    border: "border-slate-200"
  };
  return <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === "P" ? "bg-blue-500" : status === "A" ? "bg-red-500" : status === "L" ? "bg-amber-500" : "bg-slate-300"}`}></span>
      {status || "—"} · {cfg.label}
    </span>;
}
function Attendance() {
  const [currentClass, setCurrentClass] = useState("Class 1");
  const [selGroup, setSelGroup] = useState(null);
  const [selSection, setSelSection] = useState(null);
  const [selectedDate, setSelectedDate] = useState(todayISTStr());
  const [attData, setAttData] = useState({});
  const [attLoading, setAttLoading] = useState(false);
  const [attError, setAttError] = useState("");
  const isSenior = currentClass === "Class 11" || currentClass === "Class 12";
  const allList = getStudents(currentClass);
  let groupList = allList;
  if (isSenior && selGroup && selGroup !== "all_senior") {
    groupList = allList.filter(s => String(s.group || '').toLowerCase() === String(selGroup || '').toLowerCase());
  }
  let dispList = groupList;
  if (selSection && selSection !== "all_sections") {
    dispList = groupList.filter(s => String(s.section || '').toUpperCase() === String(selSection).toUpperCase());
  }

  // Pull the REAL attendance marked by the Class Advisor for the selected
  // date, straight from GET /api/attendance/:className/:date (Attendance.js
  // model — status P/A/L + remark + who marked it). This is a read-only
  // view: no toggling/marking happens here.
  useEffect(() => {
    let cancelled = false;
    async function loadAttendance() {
      if (dispList.length === 0) {
        setAttData({});
        return;
      }
      setAttLoading(true);
      setAttError("");
      try {
        const keys = [...new Set(dispList.map(s => buildAttendanceKey(currentClass, s.section, isSenior ? s.group : null)))];
        const results = await Promise.all(keys.map(k => fetch(`https://schoolcrm-fzcv.onrender.com/api/attendance/${encodeURIComponent(k)}/${selectedDate}`).then(r => r.ok ? r.json() : []).catch(() => [])));
        if (cancelled) return;
        const map = {};
        results.flat().forEach(a => {
          map[String(a.studentId)] = {
            status: a.status,
            remark: a.remark || "",
            staffName: a.staffName || ""
          };
        });
        setAttData(map);
      } catch (err) {
        if (!cancelled) setAttError("Failed to load attendance for this date.");
      } finally {
        if (!cancelled) setAttLoading(false);
      }
    }
    loadAttendance();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClass, selGroup, selSection, selectedDate]);
  const present = dispList.filter(s => attData[s._id]?.status === "P").length;
  const absent = dispList.filter(s => attData[s._id]?.status === "A").length;
  const total = dispList.length;
  const seniorGroups = SENIOR_GROUPS.map(g => ({
    key: g.label,
    label: g.label,
    icon: g.icon,
    bg: g.bg,
    text: g.text,
    border: g.border
  }));
  const availSections = getSectionsForClass(currentClass);
  const dateObj = new Date(selectedDate + "T00:00:00");
  const dayLabel = dateObj.toLocaleDateString('en-IN', {
    weekday: 'long'
  });
  const dateLabel = dateObj.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
  return <div className="animate-fade-in-up">
      <div className="flex justify-between items-start mb-6 flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800">Attendance — {dayLabel}, {dateLabel}</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">Viewing attendance marked by the Class Advisor for each class</p>
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-4 py-2.5 shadow-sm">
          <i className="fas fa-calendar-alt text-blue-600"></i>
          <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Filter by date</label>
          <input type="date" value={selectedDate} max={todayISTStr()} onChange={e => setSelectedDate(e.target.value)} className="text-sm font-semibold text-slate-700 outline-none bg-transparent" />
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        {allClasses.map(c => <button key={c} onClick={() => {
        setCurrentClass(c);
        setSelGroup(null);
        setSelSection(null);
      }} className={`px-4 py-2.5 rounded-xl text-sm font-bold border transition-all duration-200 shadow-sm ${currentClass === c ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white border-blue-600 shadow-blue-200" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}>
            {c}
          </button>)}
      </div>

      {isSenior && !selGroup && <div className="mb-6">
          <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">Select Group — {currentClass}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {seniorGroups.map(g => {
          const grpList = allList.filter(s => String(s.group || '').toLowerCase() === String(g.label || '').toLowerCase());
          const gp = grpList.filter(s => s.present).length;
          return <div key={g.key} onClick={() => setSelGroup(g.key)} className={`border ${g.border} ${g.bg} rounded-2xl p-5 cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 shadow-sm`}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">{g.icon}</span>
                    <span className={`text-base font-bold ${g.text}`}>{g.label}</span>
                  </div>
                  <div className="text-[12px] text-slate-500 font-medium">{grpList.length} students · {gp} present</div>
                  <div className={`mt-3 text-[11px] font-bold ${g.text} flex items-center gap-1`}>
                    Mark Attendance <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                  </div>
                </div>;
        })}
          </div>
          <button onClick={() => {
        setSelGroup("all_senior");
        setSelSection(null);
      }} className="mt-4 text-sm text-blue-600 font-bold hover:bg-blue-50 px-4 py-2 rounded-xl transition-all border border-blue-100">
            View All {currentClass} Students <ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" />
          </button>
        </div>}

      {(!isSenior || selGroup) && !selSection && availSections.length > 0 && <div className="mb-6">
          {isSenior && selGroup && <div className="flex items-center gap-3 mb-5">
              <button onClick={() => setSelGroup(null)} className="text-sm font-bold text-slate-500 hover:text-slate-700 px-4 py-2 rounded-xl hover:bg-slate-100 transition-all">
                <ArrowLeft className="inline w-[1em] h-[1em] align-[-0.125em]" /> Back to Groups
              </button>
              <span className="text-sm font-bold text-slate-500">|</span>
              <span className="text-sm font-bold text-slate-700">{selGroup === "all_senior" ? "All Students" : seniorGroups.find(g => g.key === selGroup)?.label}</span>
            </div>}
          <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">Select Section — {currentClass}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {availSections.map(sec => {
          const secList = groupList.filter(s => s.section === sec);
          const sp = secList.filter(s => s.present).length;
          return <div key={sec} onClick={() => setSelSection(sec)} className="bg-white border border-slate-200 rounded-2xl p-5 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 shadow-sm">
                  <div className="text-base font-bold text-slate-700 mb-3">Section {sec}</div>
                  <div className="text-[12px] text-slate-500 font-medium">{secList.length} students · {sp} present</div>
                  <div className="mt-3 text-[11px] font-bold text-blue-600 flex items-center gap-1">
                    Mark Attendance <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                  </div>
                </div>;
        })}
          </div>
          <button onClick={() => setSelSection("all_sections")} className="mt-4 text-sm text-blue-600 font-bold hover:bg-blue-50 px-4 py-2 rounded-xl transition-all border border-blue-100">
            View All Sections <ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" />
          </button>
        </div>}

      {(!isSenior || selGroup) && (selSection || availSections.length === 0) && <>
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <button onClick={() => setSelSection(null)} className="text-sm font-bold text-slate-500 hover:text-slate-700 px-4 py-2 rounded-xl hover:bg-slate-100 transition-all">
              <ArrowLeft className="inline w-[1em] h-[1em] align-[-0.125em]" /> Back to Sections
            </button>
            <span className="text-sm font-bold text-slate-500">|</span>
            {isSenior && selGroup && <span className="text-sm font-bold text-slate-700">{selGroup === "all_senior" ? "All Groups" : selGroup}</span>}
            {isSenior && selGroup && <span className="text-sm font-bold text-slate-500">|</span>}
            <span className="text-sm font-bold text-slate-700">{selSection === "all_sections" ? "All Sections" : `Section ${selSection}`}</span>
          </div>

          <GlassCard>
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-slate-50 border-b border-slate-50">
              {[{
            label: "Present",
            val: present,
            color: "text-blue-600"
          }, {
            label: "Absent",
            val: absent,
            color: "text-red-500"
          }, {
            label: "Total",
            val: total,
            color: "text-slate-700"
          }, {
            label: "Percentage",
            val: total ? Math.round(present / total * 100) + "%" : "—",
            color: "text-blue-600"
          }].map(({
            label,
            val,
            color
          }) => <div key={label} className="py-5 text-center">
                  <div className={`text-2xl sm:text-3xl font-extrabold ${color}`}><AnimatedCounter value={typeof val === 'number' ? val : 0} suffix={typeof val === 'string' && val.includes('%') ? '%' : ''} /></div>
                  <div className="text-[10px] sm:text-[11px] text-slate-400 uppercase tracking-wide mt-2 font-bold">{label}</div>
                </div>)}
            </div>

            {attError && <div className="px-6 py-3 bg-red-50 border-b border-red-100 text-red-600 text-xs font-bold">
                <i className="fas fa-exclamation-triangle mr-2"></i>{attError}
              </div>}
            {!attError && !attLoading && total > 0 && present === 0 && absent === 0 && <div className="px-6 py-3 bg-amber-50 border-b border-amber-100 text-amber-700 text-xs font-bold">
                <i className="fas fa-info-circle mr-2"></i>
                No attendance marked by the Class Advisor for {dayLabel}, {dateLabel} yet.
              </div>}

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-50 bg-gradient-to-r from-blue-50 to-white">
                    {["#", "Roll No", "Student Name", "Status", "Remark"].map(h => <th key={h} className="text-[11px] text-slate-500 font-bold uppercase tracking-wide px-6 py-4 text-left">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {attLoading ? <tr>
                      <td colSpan={5} className="px-6 py-10 text-center text-sm text-slate-400 font-medium">
                        <i className="fas fa-spinner fa-pulse mr-2"></i> Loading attendance…
                      </td>
                    </tr> : dispList.map((s, i) => {
                const rec = attData[s._id];
                return <tr key={s._id || i} className="border-b border-slate-50 hover:bg-blue-50/30 transition-colors duration-200">
                        <td className="px-6 py-3 text-xs text-slate-400 font-medium">{i + 1}</td>
                        <td className="px-6 py-3 text-xs text-slate-500 font-mono">{s.roll}</td>
                        <td className="px-6 py-3 text-sm font-bold text-slate-700">{s.name}</td>
                        <td className="px-6 py-3">
                          <AttendanceStatusBadge status={rec?.status} />
                        </td>
                        <td className="px-6 py-3 text-[12px] text-slate-600">
                          {rec?.remark ? rec.remark : <span className="text-slate-300">No remark</span>}
                        </td>
                      </tr>;
              })}
                </tbody>
              </table>
            </div>
          </GlassCard>
        </>}
    </div>;
}

// ─── TIMETABLE ────────────────────────────────────────────────────────────────
// Fetches the REAL timetable saved by the admin (TimeTable.js) from
// GET /api/timetables/:className. className is built the exact same way
// TimeTable.js saves it: "Class 1-A" for regular classes, or
// "Class 11 Bio Maths-A" for HS classes (std + group + "-" + section) — the
// backend route already knows how to match this format flexibly.

const TT_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const TT_DAY_SHORT = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat"
};
function buildTimetableKey(cls, section, group) {
  return group ? `${cls} ${group}-${section}` : `${cls}-${section}`;
}
function TimetableView({
  cls,
  section,
  group,
  onBack
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [periods, setPeriods] = useState([]);
  const [timetable, setTimetable] = useState({});
  const key = buildTimetableKey(cls, section, group);
  const backLabel = group ? `${cls} — ${group}` : `${cls} — Sections`;
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`https://schoolcrm-fzcv.onrender.com/api/timetables/${encodeURIComponent(key)}`);
        if (res.status === 404) {
          if (!cancelled) {
            setPeriods([]);
            setTimetable({});
          }
          return;
        }
        if (!res.ok) throw new Error("Failed to fetch timetable");
        const data = await res.json();
        if (!cancelled) {
          setPeriods(Array.isArray(data.periods) ? data.periods : []);
          setTimetable(data.timetable && typeof data.timetable === "object" ? data.timetable : {});
        }
      } catch (e) {
        if (!cancelled) setError(" Could not load the timetable. Is the backend running on port 5000?");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [key]);

  // Every distinct real subject actually scheduled for this class/section —
  // used for the legend chips above the grid (no hardcoded subject list).
  const subjectsUsed = [];
  const seenSubjects = new Set();
  TT_DAYS.forEach(d => {
    const dayData = timetable[d] || {};
    Object.values(dayData).forEach(cell => {
      if (cell && cell.subject && !seenSubjects.has(cell.subject)) {
        seenSubjects.add(cell.subject);
        subjectsUsed.push(cell.subject);
      }
    });
  });
  return <div className="animate-fade-in-up">
      <BackBtn onClick={onBack} label={backLabel} />
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800">
            Timetable — {cls}{group ? ` · ${group}` : ""} · Section {section}
          </h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">Weekly schedule</p>
        </div>
      </div>

      {subjectsUsed.length > 0 && <div className="flex gap-2 flex-wrap mb-6">
          {subjectsUsed.map(sub => <span key={sub} className="text-[11px] font-bold px-3 py-1.5 rounded-lg shadow-sm bg-blue-50 text-blue-700">{sub}</span>)}
        </div>}

      {loading ? <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-slate-100 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
            <div className="text-slate-500 font-bold text-lg animate-pulse">Loading Timetable...</div>
          </div>
        </div> : error ? <div className="bg-red-50 border border-red-200 text-red-600 rounded-xl px-5 py-4 text-sm font-semibold">{error}</div> : periods.length === 0 ? <GlassCard>
          <div className="py-20 text-center">
            <div className="text-5xl mb-4 animate-float"><CalendarDaysIcon className="inline w-[1em] h-[1em] align-[-0.125em]" /></div>
            <div className="text-slate-500 font-bold">No timetable has been set for this class yet</div>
            <div className="text-slate-400 text-sm mt-1">Set it up from the Timetable page in the admin panel.</div>
          </div>
        </GlassCard> : <GlassCard>
          <div className="timetable-viewport" style={{
        overflowX: 'auto'
      }}>
            <table className="w-full" style={{
          minWidth: 900
        }}>
              <thead>
                <tr className="border-b border-slate-100 bg-gradient-to-r from-blue-50 to-white">
                  <th className="text-[11px] text-slate-500 font-bold uppercase tracking-wide px-5 py-4 text-left border-r border-slate-100">Period / Day</th>
                  {TT_DAYS.map(d => <th key={d} className="text-[11px] text-slate-500 font-bold uppercase tracking-wide px-5 py-4 text-center border-r border-slate-100 last:border-r-0">{TT_DAY_SHORT[d]}</th>)}
                </tr>
              </thead>
              <tbody>
                {periods.map(period => {
              const isBreak = !!period.isBreak;
              return <tr key={period.id} className={`border-b border-slate-50 last:border-b-0 ${isBreak ? "bg-slate-50/50" : ""}`}>
                      <td className="px-5 py-3 text-[11px] font-bold text-slate-500 border-r border-slate-100 whitespace-nowrap bg-slate-50/50">
                        <div>{period.label || `Period ${period.id}`}</div>
                        {period.time && <div className="text-[10px] text-slate-400 font-medium mt-0.5">{period.time}</div>}
                      </td>
                      {TT_DAYS.map(d => {
                  if (isBreak) {
                    return <td key={d} className="px-4 py-3 border-r border-slate-50 last:border-r-0 text-center">
                              <div className="rounded-lg px-3 py-2 text-[11px] font-bold italic text-blue-300 bg-blue-50/40">{period.label || "Break"}</div>
                            </td>;
                  }
                  const cell = timetable[d] && timetable[d][period.id] || null;
                  const sub = cell?.subject || "—";
                  const cellClass = sub === "—" ? "bg-blue-50 text-blue-300 border border-blue-100 opacity-40" : "bg-blue-50 text-blue-700 border border-blue-200 shadow-sm";
                  return <td key={d} className="px-4 py-3 border-r border-slate-50 last:border-r-0 text-center">
                            <div className={`rounded-lg px-3 py-2 text-[11px] font-bold leading-tight ${cellClass}`}>
                              {sub}
                              {cell?.teacher && <div className="text-[9px] font-medium opacity-70 mt-0.5">{cell.teacher}</div>}
                            </div>
                          </td>;
                })}
                    </tr>;
            })}
              </tbody>
            </table>
          </div>
        </GlassCard>}
    </div>;
}
function TimetableSections({
  cls,
  onBack
}) {
  const [selGroup, setSelGroup] = useState(null);
  const [selSection, setSelSection] = useState(null);
  const isSenior = cls === "Class 11" || cls === "Class 12";
  // Regular classes: use the REAL sections registered students actually
  // have (same helper Students/Admissions/Attendance already use), not a
  // hardcoded ["A","B","C"] list.
  const sections = isSenior ? HS_SECTIONS : getSectionsForClass(cls);
  if (isSenior && selGroup && selSection) {
    return <TimetableView cls={cls} section={selSection} group={selGroup} onBack={() => setSelSection(null)} />;
  }
  if (isSenior && selGroup) {
    return <div className="animate-fade-in-up">
        <BackBtn onClick={() => setSelGroup(null)} label={`${cls} — Groups`} />
        <div className="mb-6">
          <h2 className="text-2xl font-extrabold text-slate-800">{cls} — {selGroup} — Sections</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">Select a section to view its timetable</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {HS_SECTIONS.map((sec, i) => {
          const col = CLASS_COLORS[i % CLASS_COLORS.length];
          return <div key={sec} onClick={() => setSelSection(sec)} className={`bg-gradient-to-br ${col.bg} border ${col.border} rounded-2xl p-6 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 shadow-sm`}>
                <div className="text-sm font-bold text-slate-700 mb-2">Section {sec}</div>
                <div className={`text-[11px] font-bold ${col.text} flex items-center gap-1`}>
                  View Timetable <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
                </div>
              </div>;
        })}
        </div>
      </div>;
  }
  if (isSenior) {
    return <div className="animate-fade-in-up">
        <BackBtn onClick={onBack} label="All Classes" />
        <div className="mb-6">
          <h2 className="text-2xl font-extrabold text-slate-800">{cls} — Select Group</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">Click a subject group, then a section, to view its timetable</p>
        </div>
        <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">Subject Groups — {cls}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
          {SENIOR_GROUPS.map((g, i) => <div key={i} onClick={() => setSelGroup(g.label)} className={`border ${g.border} ${g.bg} rounded-2xl p-6 cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1 shadow-sm`}>
              <div className="text-3xl mb-3">{g.icon}</div>
              <div className={`text-sm font-bold ${g.text} mb-3`}>{g.label}</div>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {g.subjects.map(sub => <span key={sub} className={`text-[10px] font-semibold px-2 py-1 rounded-md bg-white/70 ${g.text} border border-white/50 shadow-sm`}>{sub}</span>)}
              </div>
              <div className={`text-[11px] font-bold ${g.text} flex items-center gap-1`}>
                View Timetable <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
              </div>
            </div>)}
        </div>
      </div>;
  }
  if (selSection) return <TimetableView cls={cls} section={selSection} onBack={() => setSelSection(null)} />;
  return <div className="animate-fade-in-up">
      <BackBtn onClick={onBack} label="All Classes" />
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800">{cls} — Select Section</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">Click a section to view its timetable</p>
        </div>
      </div>

      <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-4">All Sections</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
        {sections.map((sec, i) => {
        const col = CLASS_COLORS[i % CLASS_COLORS.length];
        return <div key={sec} onClick={() => setSelSection(sec)} className={`bg-gradient-to-br ${col.bg} border ${col.border} rounded-2xl p-7 text-center cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 shadow-sm group`}>
              <div className={`w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center text-xl font-bold shadow-md ${col.bg} ${col.text} group-hover:scale-110 transition-transform duration-300`}>
                {sec.split(" ")[0]}
              </div>
              <div className="text-base font-bold text-slate-700">Section {sec}</div>
              <div className="text-[11px] text-slate-400 mt-2 mb-4 font-medium">Click to view timetable</div>
              <div className={`text-[11px] font-bold px-4 py-2 rounded-full ${col.bg} ${col.text} inline-flex items-center gap-1 group-hover:gap-2 transition-all`}>
                View Timetable <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
              </div>
            </div>;
      })}
      </div>
    </div>;
}
function Timetable() {
  const [selClass, setSelClass] = useState(null);
  if (selClass) return <TimetableSections cls={selClass} onBack={() => setSelClass(null)} />;
  return <div className="animate-fade-in-up">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-800">Timetable</h2>
          <p className="text-sm text-slate-400 mt-1 font-medium">UKG to Class 12 — Click a class to view timetable</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-5">
        {allClasses.map((cls, i) => {
        const col = CLASS_COLORS[i % CLASS_COLORS.length];
        const label = cls === "UKG" ? "UKG" : cls === "LKG" ? "LKG" : cls.replace("Class ", "C");
        const isSenior = cls === "Class 11" || cls === "Class 12";
        const sectionCount = isSenior ? SENIOR_GROUPS.length : getSectionsForClass(cls).length;
        return <div key={cls} onClick={() => setSelClass(cls)} className={`bg-gradient-to-br ${col.bg} border ${col.border} rounded-2xl p-6 text-center cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300 shadow-sm group`}>
              <div className={`w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center text-xl font-bold shadow-md ${col.bg} ${col.text} group-hover:scale-110 transition-transform duration-300`}>
                {label}
              </div>
              <div className="text-base font-bold text-slate-700">{cls}</div>
              <div className="text-[11px] text-slate-400 mt-2 font-medium">
                {isSenior ? `${sectionCount} Groups` : `${sectionCount} ${sectionCount === 1 ? "Section" : "Sections"}`}
              </div>
              {isSenior && <div className="flex justify-center gap-1.5 mt-3">
                  {SENIOR_GROUPS.map(g => <span key={g.label} className="text-lg">{g.icon}</span>)}
                </div>}
              <div className={`mt-4 text-[11px] font-bold px-4 py-2 rounded-full ${col.bg} ${col.text} inline-flex items-center gap-1 group-hover:gap-2 transition-all`}>
                View Timetable <span><ArrowRight className="inline w-[1em] h-[1em] align-[-0.125em]" /></span>
              </div>
            </div>;
      })}
      </div>
    </div>;
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────

// ─── NAVBAR STRUCTURE ──────────────────────────────────────────────────────
// Order: Dashboard | Updates (▾ Circular / Message / Timetable) |
// Finance (▾ Donation / Salary) | Attendance (▾ Student Attendance /
// Staff Attendance) | Directory (▾ Students / Staff) — then Profile +
// Logout on the right. Grouping related pages under a dropdown keeps the
// navbar from getting crowded while still reaching every page in at most
// 2 clicks.
const NAV_ITEMS = [{
  id: "dashboard",
  label: "Dashboard",
  icon: LayoutDashboard,
  page: "dashboard"
}, {
  id: "updates",
  label: "Updates",
  icon: Megaphone,
  dropdown: [{
    id: "circular",
    label: "Circular",
    page: "circular"
  }, {
    id: "adminmessages",
    label: "Message",
    external: "/principal-view"
  }, {
    id: "timetable",
    label: "Timetable",
    page: "timetable"
  }]
}, {
  id: "finance",
  label: "Finance",
  icon: Coins,
  dropdown: [{
    id: "donations",
    label: "Donation",
    page: "donations"
  }, {
    id: "salary",
    label: "Salary",
    page: "salary"
  }]
}, {
  id: "attendance",
  label: "Attendance",
  icon: CheckSquare,
  dropdown: [{
    id: "attendance",
    label: "Student Attendance",
    page: "attendance"
  }, {
    id: "staffattendance",
    label: "Staff Attendance",
    page: "staffattendance"
  }]
}, {
  id: "directory",
  label: "Directory",
  icon: Users,
  dropdown: [{
    id: "students",
    label: "Students",
    page: "students"
  }, {
    id: "staff",
    label: "Staff",
    page: "staff"
  }]
}];

// Flat list of every leaf destination — used for the mobile menu grid and
// for resolving the current page's label in the mobile header bar.
const MOBILE_NAV_ITEMS = NAV_ITEMS.flatMap(item => {
  if (item.dropdown) {
    return item.dropdown.map(child => ({
      id: child.id,
      label: `${item.label}: ${child.label}`,
      icon: item.icon,
      page: child.page,
      external: child.external
    }));
  }
  return [{
    id: item.id,
    label: item.label,
    icon: item.icon,
    page: item.page
  }];
});
function getActivePageLabel(activePage) {
  for (const item of NAV_ITEMS) {
    if (item.page === activePage) return item.label;
    if (item.dropdown) {
      const child = item.dropdown.find(d => d.page === activePage);
      if (child) return `${item.label} — ${child.label}`;
    }
  }
  return "Dashboard";
}
export default function SchoolCRM() {
  const schoolName = useSchoolName();
  const [activePage, setActivePage] = useState("dashboard");
  const [principalName, setPrincipalName] = useState("");
  const [showPrincipal, setShowPrincipal] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);
  const handleLogout = () => {
    localStorage.removeItem("role");
    localStorage.removeItem("userId");
    window.location.href = "/login";
  };

  // Close any open navbar dropdown when clicking outside the nav.
  useEffect(() => {
    function onDocClick(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);
  useEffect(() => {
    injectStyles();
    const s = document.getElementById('pp-script');
    if (s) s.remove();
    const r = document.getElementById('parent-portal-root');
    if (r) r.innerHTML = '';
    (async () => {
      try {
        const res = await fetch("https://schoolcrm-fzcv.onrender.com/api/headmistress");
        if (res.ok) {
          const result = await res.json();
          if (result.data?.name) setPrincipalName(result.data.name);
        }
      } catch {}
    })();
  }, []);
  const renderPage = () => {
    switch (activePage) {
      case "dashboard":
        return <Dashboard navigate={setActivePage} />;
      case "admissions":
        return <Admissions />;
      case "students":
        return <Students />;
      case "staff":
        return <Staff />;
      case "donations":
        return <DonationsPage onBack={() => setActivePage("dashboard")} />;
      case "circular":
        return <Circular />;
      case "attendance":
        return <Attendance />;
      case "timetable":
        return <Timetable />;
      case "staffattendance":
        return <StaffAttendancePage onBack={() => setActivePage("dashboard")} />;
      case "salary":
        return <PrincipalSalaryView onBack={() => setActivePage("dashboard")} />;
      default:
        return <Dashboard navigate={setActivePage} />;
    }
  };
  return <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-blue-50/30 font-sans overflow-x-hidden flex flex-col relative">
      {/* Animated Background Circles for entire app */}
      <AnimatedBackgroundCircles />
      
      <div className="relative z-10">
        <header className="sticky top-0 z-40 bg-gradient-to-r from-blue-900 via-blue-900 to-blue-900 shadow-2xl">
          <div className="w-full px-4 md:px-6 lg:px-8 flex items-center gap-3 h-16 lg:h-[68px]">
            <div className="flex items-center gap-3 shrink-0">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30 shrink-0 shine-hover overflow-hidden p-1">
                <img src="/school-logo.png" alt="School Logo" className="w-full h-full object-contain" />
              </div>
              <div className="hidden sm:block min-w-0">
                <div className="text-base font-extrabold text-white leading-none tracking-tight truncate">{schoolName}</div>
                <div className="text-[10px] text-blue-300 mt-1 font-medium truncate">
                  {principalName ? principalName : "Admin Portal"}
                </div>
              </div>
            </div>

            <nav ref={navRef} className="hidden lg:flex items-center gap-1 flex-1 min-w-0 ml-4 relative">
              {NAV_ITEMS.map(item => {
              const Icon = item.icon;
              if (item.dropdown) {
                const isActive = item.dropdown.some(d => d.page === activePage);
                const isOpen = openDropdown === item.id;
                return <div key={item.id} className="relative shrink-0">
                      <button onClick={() => setOpenDropdown(isOpen ? null : item.id)} className={`flex items-center gap-1.5 px-3.5 py-2.5 rounded-xl text-sm font-bold tracking-wide whitespace-nowrap transition-all duration-200 shrink-0 ${isActive ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-900/40" : "text-blue-200 hover:bg-white/10 hover:text-white"}`}>
                        <Icon size={16} strokeWidth={2.3} className="shrink-0" />
                        <span>{item.label}</span>
                        <ChevronDown size={13} strokeWidth={2.6} className={`shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                      </button>

                      {isOpen && <div className="absolute left-0 top-[calc(100%+8px)] min-w-[200px] bg-white rounded-xl shadow-2xl shadow-blue-900/30 border border-slate-100 overflow-hidden z-50 animate-fade-in-scale">
                          {item.dropdown.map(child => {
                      const childActive = child.page && child.page === activePage;
                      return <button key={child.id} onClick={() => {
                        setOpenDropdown(null);
                        if (child.external) {
                          window.location.href = child.external;
                        } else {
                          setActivePage(child.page);
                        }
                      }} className={`w-full text-left px-4 py-3 text-sm font-semibold transition-colors duration-150 ${childActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-blue-50 hover:text-blue-700"}`}>
                                {child.label}
                              </button>;
                    })}
                        </div>}
                    </div>;
              }
              const isActive = activePage === item.page;
              return <button key={item.id} onClick={() => {
                setOpenDropdown(null);
                setActivePage(item.page);
              }} className={`flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-sm font-bold tracking-wide whitespace-nowrap transition-all duration-200 shrink-0 ${isActive ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-900/40" : "text-blue-200 hover:bg-white/10 hover:text-white"}`}>
                    <Icon size={16} strokeWidth={2.3} className="shrink-0" />
                    <span>{item.label}</span>
                  </button>;
            })}
            </nav>

            <div className="hidden lg:flex items-center gap-2 shrink-0 ml-auto">
              <button onClick={() => setShowPrincipal(true)} className="flex items-center gap-2 px-3.5 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 text-blue-100 rounded-xl text-sm font-bold transition-all duration-200 shrink-0">
                <UserCircle size={17} strokeWidth={2.2} />
                <span>Profile</span>
              </button>
              <button onClick={handleLogout} className="flex items-center gap-2 px-3.5 py-2.5 bg-white/5 hover:bg-red-500/15 border border-white/10 hover:border-red-400/30 text-blue-100 hover:text-red-200 rounded-xl text-sm font-bold transition-all duration-200 shrink-0">
                <LogOut size={17} strokeWidth={2.2} />
                <span>Logout</span>
              </button>
            </div>

            <div className="flex lg:hidden items-center justify-between flex-1 min-w-0">
              <div className="text-sm font-bold text-blue-200 truncate ml-1">
                {getActivePageLabel(activePage)}
              </div>
              <button onClick={() => setMobileOpen(o => !o)} aria-label={mobileOpen ? "Close menu" : "Open menu"} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 border border-white/10 text-white shadow-sm hover:bg-white/20 shrink-0 mobile-tap-target">
                {mobileOpen ? <X size={18} /> : <Menu size={18} />}
                {mobileOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
          </div>

          {mobileOpen && <div className="lg:hidden border-t border-white/10 bg-gradient-to-b from-blue-900 via-blue-900 to-blue-900 shadow-2xl max-h-[75vh] overflow-y-auto sidebar-scroll">
              <nav className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {MOBILE_NAV_ITEMS.map(p => {
              const isActive = p.page && p.page === activePage;
              const Icon = p.icon;
              return <button key={p.id} onClick={() => {
                setMobileOpen(false);
                if (p.external) {
                  window.location.href = p.external;
                  return;
                }
                setActivePage(p.page);
              }} className={`flex items-center gap-2 px-3.5 py-3 rounded-xl text-sm font-bold transition-all duration-200 mobile-tap-target ${isActive ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg" : "text-blue-200 bg-white/5 hover:bg-white/10"}`}>
                      <Icon size={16} strokeWidth={2.3} className="shrink-0" />
                      <span className="truncate">{p.label}</span>
                    </button>;
            })}
              </nav>
              <div className="px-3 pb-3 flex flex-col gap-2">
                <button onClick={() => {
              setShowPrincipal(true);
              setMobileOpen(false);
            }} className="w-full flex items-center gap-2 justify-center px-3.5 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-blue-100 rounded-xl text-sm font-bold transition-all duration-200 mobile-tap-target">
                  <UserCircle size={17} strokeWidth={2.2} />
                  <span>Profile</span>
                </button>
                <button onClick={() => {
              handleLogout();
              setMobileOpen(false);
            }} className="w-full flex items-center gap-2 justify-center px-3.5 py-3 bg-white/5 hover:bg-red-500/15 border border-white/10 hover:border-red-400/30 text-blue-100 hover:text-red-200 rounded-xl text-sm font-bold transition-all duration-200 mobile-tap-target">
                  <LogOut size={17} strokeWidth={2.2} />
                  <span>Logout</span>
                </button>
              </div>
            </div>}
        </header>

        <div className="flex-1 min-w-0 w-full px-4 md:px-6 lg:px-8 py-6 md:py-8">{renderPage()}</div>

        {showPrincipal && <PrincipalDetailsModal onClose={() => setShowPrincipal(false)} />}
      </div>
    </div>;
}
function normalizeApplyingClass(raw) {
  if (!raw && raw !== 0) return 'Class 1';
  let v = String(raw).trim();
  if (!v) return 'Class 1';
  if (/^ukg$/i.test(v)) return 'UKG';
  if (/^lkg$/i.test(v)) return 'LKG';
  const mClass = v.match(/class\s*(\d{1,2})/i);
  if (mClass) return 'Class ' + parseInt(mClass[1], 10);
  const mNum = v.match(/(\d{1,2})/);
  if (mNum) return 'Class ' + parseInt(mNum[1], 10);
  return v.split(' ').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
}